import { Module } from '../../types';
import { llmFoundationsModule1QuestionBank } from '../questionBanks/llmFoundationsModule1Questions';

export const llmFoundationsModule1: Module = {
  id: 'llm-foundations-module-1',
  title: 'Module 1: Conceptual Foundation - What, Why, and How of LLMs',
  description:
    'A comprehensive foundation covering everything you need to know about Large Language Models - from basic concepts and evolution, through their position in the AI landscape and societal impact, to core technical concepts like tokens, attention, and generation.',
  duration: '8-10 hours',
  content: `# Module 1: Conceptual Foundation - What, Why, and How of LLMs

## 🎯 Module Overview

This comprehensive module provides a complete conceptual foundation for understanding Large Language Models (LLMs). We'll journey from the basics of what LLMs are, through their evolution and capabilities, explore their broader impact on society and business, and dive into the core technical concepts that make them work.

### What You'll Learn

#### Part 1: Introduction to Language Models
- What LLMs are and how they differ from traditional software
- The evolution from rule-based systems to modern transformers
- Core capabilities and fundamental limitations
- Real-world applications across industries

#### Part 2: The Big Picture
- LLMs' position in the broader AI landscape
- Business and economic implications
- Societal impacts and transformations
- Ethical considerations and responsible AI

#### Part 3: Core Concepts
- Tokens and tokenization
- How LLMs "understand" language
- Prediction and generation mechanisms
- Context windows and attention mechanisms

---

# Part 1: Introduction to Language Models

## 🎯 Learning Objectives
By the end of this section, you will be able to:
- Define what Large Language Models (LLMs) are in simple terms
- Trace the evolution from traditional NLP to modern LLMs
- Identify key capabilities and limitations of LLMs
- Recognize real-world applications across different industries
- Understand the fundamental difference between LLMs and traditional software

---

## 📚 Section 1: What are Large Language Models?

### Definition
A **Large Language Model (LLM)** is an artificial intelligence system that has been trained on vast amounts of text data to understand, generate, and work with human language. Think of it as a highly sophisticated pattern recognition system that has "read" billions of pages of text and learned the statistical patterns of how language works.

### Key Characteristics

#### 1. **Large Scale**
- **Parameters**: Modern LLMs contain billions to trillions of parameters (adjustable values that store learned patterns)
- **Training Data**: Trained on terabytes of text from books, websites, articles, and other sources
- **Computational Requirements**: Require massive computational resources for training

#### 2. **Language Understanding**
- Can process and "understand" context across long passages
- Recognize nuances, ambiguities, and implied meanings
- Handle multiple languages and various writing styles

#### 3. **Generative Capabilities**
- Create new, coherent text that follows learned patterns
- Adapt writing style to match different contexts
- Generate creative content while maintaining logical consistency

### The "Model" Concept
A model is essentially a mathematical function that:
1. Takes text as input
2. Processes it through learned patterns
3. Produces relevant output (predictions, completions, or transformations)

### Analogy: The Ultimate Pattern Learner
Imagine a student who has read every book in every library in the world. This student doesn't memorize everything word-for-word but instead learns:
- How sentences are typically structured
- What words usually follow other words
- How ideas connect and flow
- Different writing styles and contexts

When you ask this student a question, they use all these learned patterns to provide a relevant, coherent response. That's essentially what an LLM does, but with mathematical patterns instead of human understanding.

---

## 📚 Section 2: Evolution from Traditional NLP to Modern LLMs

### Timeline of Language Processing

#### **1950s-1960s: Rule-Based Systems**
- **Approach**: Hand-coded rules and dictionaries
- **Example**: "If word ends in '-ing', it's probably a verb"
- **Limitations**: Couldn't handle language complexity and exceptions
- **Scale**: Hundreds of rules

#### **1980s-1990s: Statistical Methods**
- **Approach**: Probability-based models using small datasets
- **Example**: N-gram models predicting next word based on previous 2-3 words
- **Breakthrough**: Moved from rules to learning from data
- **Scale**: Thousands to millions of parameters

#### **2000s: Machine Learning Era**
- **Approach**: Support Vector Machines, Random Forests for specific NLP tasks
- **Example**: Separate models for sentiment analysis, translation, etc.
- **Innovation**: Feature engineering and task-specific optimization
- **Scale**: Task-specific models with limited transfer learning

#### **2010s: Deep Learning Revolution**
- **2013**: Word2Vec - Words as vectors capturing semantic meaning
- **2014**: Sequence-to-sequence models for translation
- **2015**: Attention mechanisms - "focusing" on relevant parts
- **2017**: **Transformer Architecture** - The foundation of modern LLMs
- **Scale**: Millions to hundreds of millions of parameters

#### **2018-Present: The LLM Era**
- **2018**: BERT (340M parameters) - Bidirectional understanding
- **2019**: GPT-2 (1.5B parameters) - Impressive text generation
- **2020**: GPT-3 (175B parameters) - Few-shot learning breakthrough
- **2022**: ChatGPT - Making LLMs accessible to everyone
- **2023-2024**: GPT-4, Claude, Gemini - Multimodal and reasoning capabilities
- **Scale**: Hundreds of billions to trillions of parameters

### Key Paradigm Shifts

#### From Narrow to General
- **Before**: Separate model for each task (translation, summarization, Q&A)
- **Now**: Single model handles multiple tasks through prompting

#### From Supervised to Self-Supervised
- **Before**: Required labeled data for each specific task
- **Now**: Learn from raw text without explicit labels

#### From Small to Large
- **Before**: Models fit on a single computer
- **Now**: Require distributed computing clusters

#### From Deterministic to Probabilistic
- **Before**: Rule-based systems with predictable outputs
- **Now**: Probabilistic models with variable outputs

### The Transformer Breakthrough

The 2017 paper "Attention Is All You Need" introduced the transformer architecture, which revolutionized NLP by:

1. **Parallel Processing**: Unlike RNNs that process sequentially, transformers process all tokens simultaneously
2. **Attention Mechanism**: Can focus on relevant parts of input regardless of distance
3. **Scalability**: Architecture that benefits from increased scale
4. **Transfer Learning**: Pre-train once, fine-tune for many tasks

---

## 📚 Section 3: Core Capabilities of LLMs

### 1. Natural Language Understanding

#### Context Comprehension
- Understanding meaning from surrounding text
- Maintaining coherence across long passages
- Interpreting ambiguous statements based on context

#### Semantic Understanding
- Grasping implied meanings and subtext
- Understanding metaphors and analogies
- Recognizing sarcasm and humor (to some extent)

#### Multi-turn Dialogue
- Maintaining conversation context
- Referencing previous statements
- Adapting responses based on conversation flow

### 2. Text Generation

#### Creative Writing
- Stories, poems, scripts, and narratives
- Different styles and genres
- Character dialogue and descriptions

#### Technical Writing
- Documentation and tutorials
- Reports and analyses
- Academic papers (with caveats)

#### Code Generation
- Programming in multiple languages
- Debugging and explaining code
- Converting between languages

### 3. Knowledge Synthesis

#### Information Integration
- Combining information from training data
- Creating summaries and overviews
- Connecting related concepts

#### Reasoning (Limited)
- Drawing logical conclusions
- Pattern recognition and inference
- Step-by-step problem solving (with limitations)

#### Explanation
- Breaking down complex topics
- Providing examples and analogies
- Teaching and tutoring

### 4. Language Translation

#### Multi-lingual Support
- Understanding text in numerous languages
- Translating between language pairs
- Maintaining context and nuance (mostly)

#### Cross-lingual Transfer
- Applying knowledge across languages
- Understanding concepts independent of language
- Code-switching capabilities

### 5. Task Adaptation

#### Few-shot Learning
- Learning new tasks from just a few examples
- Adapting to specific formats quickly
- Pattern recognition from minimal data

#### Zero-shot Learning
- Performing tasks without any examples
- Following instructions directly
- Generalizing from training knowledge

#### In-context Learning
- Adapting behavior based on prompt context
- Following specific guidelines
- Mimicking provided styles

---

## 📚 Section 4: Limitations and Challenges

### 1. Knowledge Limitations

#### Knowledge Cutoff
- Models only know information from their training data
- No real-time or post-training information access
- Cannot browse the internet or access external databases
- Unaware of events after training cutoff date

#### Factual Accuracy
- Can be confidently wrong about facts
- May mix up details or dates
- No inherent fact-checking mechanism
- Susceptible to training data errors

### 2. Hallucination

#### What It Is
- Generating plausible-sounding but false information
- Creating fictional details when uncertain
- Mixing real and imagined elements

#### Why It Happens
- Pattern matching without true understanding
- Pressure to provide complete answers
- Statistical generation of "likely" content
- No grounding in reality

#### Impact
- Misinformation spread
- Trust and reliability issues
- Need for human verification
- Limits on autonomous use

### 3. Reasoning Limitations

#### Mathematical Reasoning
- Struggles with complex calculations
- May make arithmetic errors
- Limited symbolic manipulation
- Inconsistent performance on math problems

#### Logical Reasoning
- Can fail at simple logic puzzles
- Inconsistent application of rules
- Limited causal understanding
- May contradict itself

#### Common Sense
- Lacks real-world experience
- May suggest physically impossible things
- Limited understanding of cause and effect
- No embodied knowledge

### 4. Context Window Limitations

#### Fixed Context Size
- Limited amount of text that can be processed at once
- Typically 4K to 128K tokens (varies by model)
- Cannot maintain infinite conversation history

#### Information Loss
- May lose track of earlier parts of long conversations
- Important details can be "forgotten"
- Difficulty with very long documents

#### Management Challenges
- Need to prioritize what stays in context
- Summarization may lose nuance
- Chunking strategies required for long texts

### 5. Consistency Issues

#### Response Variability
- Same prompt can produce different outputs
- Probabilistic nature means inherent randomness
- May give contradictory answers

#### Lack of Persistent Memory
- No memory between conversations
- Cannot learn from interactions
- Each session starts fresh

#### Opinion and Stance
- May appear to have different "opinions"
- Inconsistent personality or voice
- Influenced by prompt phrasing

### 6. Lack of True Understanding

#### No Consciousness
- No self-awareness or sentience
- No genuine comprehension
- No emotions or experiences

#### Surface-Level Processing
- Pattern matching rather than understanding
- No deep semantic knowledge
- Cannot truly "know" things

#### No Real-World Grounding
- Never experienced physical reality
- No sensory input or embodiment
- Abstract knowledge only

---

## 📚 Section 5: Real-World Applications

### Healthcare

#### Clinical Decision Support
- Analyzing patient symptoms and history
- Suggesting potential diagnoses (with physician oversight)
- Identifying drug interactions and contraindications
- Providing evidence-based treatment options

#### Medical Documentation
- Automating clinical note generation
- Summarizing patient records
- Extracting key information from medical literature
- Generating discharge summaries

#### Medical Research
- Literature review and synthesis
- Hypothesis generation
- Clinical trial protocol development
- Pattern identification in medical data

#### Patient Communication
- Automated health assistants for basic queries
- Medication reminders and instructions
- Mental health support chatbots (with limitations)
- Health education and information

### Education

#### Personalized Tutoring
- Adaptive learning systems
- One-on-one tutoring at scale
- Customized explanations based on student level
- Practice problem generation

#### Content Creation
- Generating educational materials
- Creating quizzes and assessments
- Developing course outlines
- Writing textbook supplements

#### Assessment and Feedback
- Automated essay grading (with human review)
- Providing detailed feedback on assignments
- Identifying learning gaps
- Progress tracking and reporting

#### Language Learning
- Conversation practice
- Grammar explanations
- Vocabulary building
- Cultural context education

### Business and Enterprise

#### Customer Service
- Intelligent chatbots for support
- FAQ automation
- Ticket routing and prioritization
- Sentiment analysis of customer feedback

#### Content Marketing
- Blog post generation
- Social media content creation
- Email marketing campaigns
- SEO optimization

#### Data Analysis
- Natural language interfaces for databases
- Report generation from data
- Trend identification and insights
- Predictive analytics assistance

#### Human Resources
- Resume screening and matching
- Interview question generation
- Employee onboarding materials
- Policy documentation

### Technology and Software Development

#### Code Generation
- GitHub Copilot and similar tools
- Boilerplate code creation
- Algorithm implementation
- Code translation between languages

#### Documentation
- API documentation generation
- Code commenting
- README file creation
- Technical specification writing

#### Testing and Debugging
- Test case generation
- Bug report analysis
- Code review assistance
- Error message interpretation

#### DevOps and Automation
- Script generation
- Configuration file creation
- Deployment automation
- Log analysis

### Creative Industries

#### Writing and Publishing
- Story and plot development
- Character creation
- Editing and proofreading
- Style adaptation

#### Marketing and Advertising
- Ad copy generation
- Campaign ideation
- Brand messaging
- Market research synthesis

#### Entertainment
- Game dialogue and narratives
- Script writing assistance
- Interactive storytelling
- Virtual character personalities

#### Design and Art
- Concept descriptions
- Creative briefs
- Art direction documentation
- Style guides

### Legal and Compliance

#### Legal Research
- Case law analysis
- Statute interpretation
- Legal document summarization
- Precedent identification

#### Contract Analysis
- Contract review and summarization
- Clause identification
- Risk assessment
- Compliance checking

#### Document Generation
- Standard legal documents
- Terms and conditions
- Privacy policies
- Disclosure statements

### Financial Services

#### Market Analysis
- Financial report summarization
- Trend analysis
- News sentiment analysis
- Market commentary generation

#### Customer Support
- Account inquiry handling
- Transaction explanation
- Product recommendations
- Financial literacy education

#### Risk Assessment
- Fraud detection assistance
- Credit analysis support
- Regulatory compliance checking
- Due diligence documentation

---

## 📚 Section 6: LLMs vs Traditional Software

### Traditional Software

#### Characteristics
- **Deterministic**: Same input always produces same output
- **Rule-Based**: Follows explicitly programmed logic
- **Predictable**: Behavior is fully specified by code
- **Limited Scope**: Can only do what it's programmed to do
- **Debugging**: Can trace exact execution path
- **Testing**: Can verify all code paths

#### Strengths
- Reliability and consistency
- Precise control
- Predictable performance
- Clear accountability
- Efficient for specific tasks

#### Limitations
- Inflexible to new situations
- Requires explicit programming for each case
- Cannot handle ambiguity well
- Limited natural language processing

### Large Language Models

#### Characteristics
- **Probabilistic**: Outputs vary based on probability distributions
- **Pattern-Based**: Learns from data rather than explicit rules
- **Emergent Behavior**: Capabilities emerge from scale
- **Flexible**: Can adapt to new tasks through prompting
- **Opaque**: Difficult to trace decision-making
- **Approximate**: Provides "best guess" answers

#### Strengths
- Handles natural language naturally
- Adapts to new tasks without reprogramming
- Can process ambiguous inputs
- Generalizes from training data
- Creative and flexible outputs

#### Limitations
- Unpredictable edge cases
- No guaranteed correctness
- Difficult to debug
- Can hallucinate information
- Requires significant resources

### Hybrid Approaches

#### Combining Strengths
- Use LLMs for natural language interface
- Traditional software for critical logic
- LLMs for flexibility, software for reliability
- Human oversight for important decisions

#### Examples
- LLM generates SQL, database executes it
- LLM suggests actions, rule engine validates
- LLM provides options, traditional algorithm optimizes
- LLM interprets input, software performs calculation

---

# Part 2: The Big Picture

## 🎯 Learning Objectives
By the end of this section, you will be able to:
- Position LLMs within the broader artificial intelligence landscape
- Compare LLMs with other AI approaches and understand their unique strengths
- Analyze business and economic implications of LLM adoption
- Evaluate societal impacts and transformative potential
- Understand key ethical considerations and challenges
- Develop a framework for thinking about responsible AI deployment

---

## 📚 Section 1: LLMs in the AI Landscape

### The AI Hierarchy

#### Level 1: Narrow AI (Artificial Narrow Intelligence - ANI)
**Definition**: AI systems designed for specific tasks
- **Examples**: Chess engines, spam filters, recommendation systems
- **LLMs Position**: LLMs are currently here, but pushing boundaries
- **Characteristics**: 
  - Excel at specific domains
  - No general intelligence
  - Cannot transfer knowledge broadly

#### Level 2: General AI (Artificial General Intelligence - AGI)
**Definition**: AI matching human cognitive abilities across all domains
- **Status**: Not yet achieved
- **LLMs Contribution**: Stepping stone toward AGI
- **Requirements**:
  - Reasoning and planning
  - Learning from experience
  - Understanding causality
  - Consciousness (debated)

#### Level 3: Super AI (Artificial Superintelligence - ASI)
**Definition**: AI surpassing human intelligence in all domains
- **Status**: Theoretical/speculative
- **Timeline**: Uncertain, estimates vary widely
- **Implications**: Transformative or existential

### AI Paradigms and Where LLMs Fit

#### 1. **Symbolic AI (1950s-1980s)**
- **Approach**: Logic, rules, and symbols
- **Strengths**: Explainable, precise
- **Weaknesses**: Brittle, doesn't handle uncertainty
- **LLMs Comparison**: LLMs learn patterns vs. explicit rules

#### 2. **Machine Learning (1990s-present)**
- **Approach**: Learning from data
- **Categories**:
  - Supervised Learning
  - Unsupervised Learning
  - Reinforcement Learning
- **LLMs Position**: Self-supervised learning at massive scale

#### 3. **Deep Learning (2010s-present)**
- **Approach**: Multi-layer neural networks
- **Applications**: Computer vision, speech, NLP
- **LLMs Role**: Transformer architecture breakthrough

#### 4. **Foundation Models (2020s-present)**
- **Definition**: Large models trained on broad data, adapted for many tasks
- **Examples**: LLMs, vision models (CLIP), multimodal models
- **Paradigm Shift**: One model, many applications

### The AI Ecosystem

\`\`\`
AI Ecosystem Map:

├── Research & Development
│   ├── Academic Institutions (Stanford, MIT, etc.)
│   ├── Corporate Labs (Google DeepMind, OpenAI, Anthropic)
│   └── Open Source Communities
│
├── Infrastructure
│   ├── Hardware (NVIDIA GPUs, Google TPUs)
│   ├── Cloud Platforms (AWS, Azure, GCP)
│   └── Development Frameworks (PyTorch, TensorFlow)
│
├── Models & Applications
│   ├── Foundation Models (GPT, Claude, Gemini)
│   ├── Specialized Models (Domain-specific)
│   └── Application Layer (ChatGPT, Copilot, etc.)
│
└── End Users
    ├── Enterprises
    ├── Developers
    └── Consumers
\`\`\`

---

## 📚 Section 2: Comparison with Other AI Approaches

### LLMs vs. Traditional Machine Learning

| Aspect | Traditional ML | LLMs |
|--------|---------------|------|
| **Data Requirements** | Structured, labeled data | Raw text, self-supervised |
| **Task Specificity** | One model per task | Multi-task capable |
| **Training Process** | Task-specific optimization | Pre-training + fine-tuning |
| **Interpretability** | Often interpretable | Black box |
| **Deployment** | Lightweight | Resource intensive |
| **Flexibility** | Fixed input/output | Natural language interface |

### LLMs vs. Expert Systems

#### Expert Systems
- **Strengths**: Explainable decisions, guaranteed consistency
- **Weaknesses**: Expensive to build, brittle, narrow domain
- **Use Cases**: Medical diagnosis, financial auditing

#### LLMs
- **Strengths**: Broad knowledge, flexible, learns from data
- **Weaknesses**: Can hallucinate, not always explainable
- **Better For**: Open-ended tasks, creative work, general assistance

### LLMs vs. Computer Vision Models

#### Similarities
- Both use deep learning
- Transfer learning approaches
- Require large datasets
- GPU-intensive training

#### Differences
| Computer Vision | LLMs |
|----------------|------|
| Spatial patterns | Sequential patterns |
| Pixels to labels | Tokens to tokens |
| Object detection, segmentation | Generation, understanding |
| Convolutional architectures | Transformer architectures |

### LLMs vs. Reinforcement Learning

#### Reinforcement Learning (RL)
- **Learning**: Through trial and error
- **Optimization**: Maximize reward signal
- **Applications**: Game playing, robotics, recommendation
- **Example**: AlphaGo, autonomous driving

#### LLMs
- **Learning**: From existing text
- **Optimization**: Predict next token
- **Applications**: Text generation, understanding
- **Integration**: RLHF combines both approaches

### Hybrid Approaches: The Future

#### Multimodal Models
- Combine vision + language (GPT-4V, Gemini)
- Enable richer understanding
- Bridge different AI domains

#### Neuro-Symbolic AI
- Combine neural networks + symbolic reasoning
- Best of both worlds
- Active research area

#### Tool-Using AI
- LLMs calling external tools
- Combines reasoning with computation
- Examples: WebGPT, Toolformer

---

## 📚 Section 3: Business and Economic Implications

### Economic Impact Projections

#### Market Size
- **2024**: $15 billion (estimated)
- **2030**: $260 billion (projected)
- **Growth Rate**: 35-40% CAGR

#### Value Creation Areas
1. **Productivity Gains**: $2.6-4.4 trillion annually by 2030 (McKinsey)
2. **New Business Models**: AI-first companies
3. **Cost Reduction**: Automation of knowledge work
4. **Innovation Acceleration**: Faster R&D cycles

### Industry Transformation

#### 1. **Knowledge Work Revolution**
**Impact Areas**:
- Writing and content creation
- Research and analysis
- Coding and software development
- Customer service

**Transformation Pattern**:
\`\`\`
Before: Human writes document (4 hours)
With LLM: Human guides + edits AI draft (1 hour)
Result: 4x productivity increase
\`\`\`

#### 2. **Business Model Disruption**

**Traditional Model**:
- Large teams for content/code
- Slow iteration cycles
- High operational costs

**LLM-Enabled Model**:
- Small teams with AI augmentation
- Rapid prototyping
- Lower marginal costs
- Personalization at scale

#### 3. **Competitive Dynamics**

**First Mover Advantages**:
- Early adoption benefits
- Data accumulation
- Customer lock-in

**Democratization Effects**:
- Small companies compete with large
- Individual creators empowered
- Global talent accessibility

### Enterprise Adoption Patterns

#### Phase 1: Experimentation (2022-2023)
- Pilot projects
- Risk assessment
- Use case identification

#### Phase 2: Integration (2024-2025)
- Production deployments
- Process redesign
- Training programs

#### Phase 3: Transformation (2026+)
- AI-native workflows
- New job roles
- Business model evolution

### Return on Investment (ROI)

#### Cost Structure
**Initial Investment**:
- Model licensing/API costs
- Infrastructure setup
- Training and change management
- Integration development

**Ongoing Costs**:
- Per-token/query pricing
- Maintenance and updates
- Quality assurance
- Human oversight

#### Value Drivers
1. **Labor Augmentation**: 30-70% productivity gains
2. **Quality Improvement**: Fewer errors, better consistency
3. **Speed to Market**: Faster development cycles
4. **Customer Satisfaction**: 24/7 availability, personalization

#### ROI Calculation Example
\`\`\`
Customer Service Automation:
- Cost Reduction: 60% fewer human agents needed
- Response Time: 90% faster resolution
- Satisfaction: 15% increase in CSAT scores
- ROI: 300% in year one
\`\`\`

---

## 📚 Section 4: Societal Impacts

### Positive Transformations

#### 1. **Democratization of Intelligence**
- **Access**: Expert knowledge available to everyone
- **Language**: Breaking down language barriers globally
- **Education**: Personalized learning for all
- **Opportunity**: Leveling playing fields

#### 2. **Acceleration of Progress**
- **Scientific Research**: Faster hypothesis generation
- **Medical Advances**: Drug discovery, diagnosis assistance
- **Climate Solutions**: Optimizing renewable energy, materials
- **Space Exploration**: Analyzing vast datasets

#### 3. **Creativity Renaissance**
- **Art**: New forms of expression
- **Writing**: Overcoming creative blocks
- **Music**: AI-assisted composition
- **Design**: Rapid prototyping

#### 4. **Accessibility Revolution**
- **Disabilities**: Better assistive technologies
- **Elderly**: Companionship and assistance
- **Rural Areas**: Access to expertise
- **Developing Nations**: Leapfrogging infrastructure

### Challenges and Risks

#### 1. **Labor Market Disruption**

**At Risk Jobs**:
- Content writers
- Junior programmers
- Data analysts
- Customer service
- Translators

**Emerging Roles**:
- AI trainers
- Prompt engineers
- AI ethicists
- Human-AI collaboration specialists

**Transition Challenges**:
- Retraining needs
- Income inequality
- Social safety nets

#### 2. **Information Ecosystem**

**Misinformation Risks**:
- Deepfakes at scale
- Convincing false narratives
- Information overload
- Truth verification challenges

**Mitigation Strategies**:
- Digital watermarking
- Detection tools
- Media literacy education
- Regulatory frameworks

#### 3. **Social and Psychological Impact**

**Concerns**:
- Reduced human interaction
- Skill atrophy
- Over-dependence on AI
- Identity and purpose questions

**Opportunities**:
- More time for human connection
- Focus on uniquely human skills
- Enhanced creativity
- Reduced mundane work

### Digital Divide Considerations

#### Access Inequality
- **Compute Resources**: High costs exclude many
- **Internet Requirements**: Bandwidth limitations
- **Language Support**: English bias in models
- **Technical Literacy**: Skills gap

#### Potential Solutions
- Open source models
- Edge deployment
- Multilingual development
- Education initiatives

---

## 📚 Section 5: Ethical Considerations

### Core Ethical Principles

#### 1. **Beneficence**
- Maximize benefits to humanity
- Improve quality of life
- Advance knowledge and capabilities
- Solve global challenges

#### 2. **Non-Maleficence**
- "Do no harm" principle
- Prevent misuse
- Minimize negative consequences
- Safety-first approach

#### 3. **Autonomy**
- Respect human agency
- Informed consent
- User control
- Transparency

#### 4. **Justice**
- Fair distribution of benefits
- Avoid discrimination
- Address bias
- Equal access

### Major Ethical Challenges

#### 1. **Bias and Fairness**

**Sources of Bias**:
- Training data reflects societal biases
- Underrepresentation of minorities
- Historical prejudices encoded
- Annotation bias

**Manifestations**:
- Gender stereotypes in language
- Racial bias in descriptions
- Cultural assumptions
- Socioeconomic prejudices

**Mitigation Approaches**:
- Diverse training data
- Bias detection tools
- Regular auditing
- Inclusive development teams

#### 2. **Privacy and Data Protection**

**Concerns**:
- Training on personal data
- Memorization of private information
- Inference of sensitive attributes
- Data leakage risks

**Best Practices**:
- Data anonymization
- Differential privacy
- User consent mechanisms
- Right to be forgotten

#### 3. **Transparency and Explainability**

**Challenges**:
- Black box nature
- Complex decision paths
- Emergent behaviors
- Attribution difficulties

**Solutions**:
- Model cards
- Interpretability research
- Documentation standards
- User education

#### 4. **Accountability and Liability**

**Questions**:
- Who is responsible for AI errors?
- How to assign liability?
- Insurance and legal frameworks?
- Redress mechanisms?

**Frameworks Developing**:
- EU AI Act
- US AI Bill of Rights
- Industry standards
- Professional codes

### Environmental Considerations

#### Carbon Footprint
- **Training**: Massive energy consumption
- **Inference**: Ongoing operational costs
- **Data Centers**: Cooling and infrastructure

#### Sustainability Measures
- Renewable energy usage
- Efficient architectures
- Carbon offsetting
- Green computing initiatives

### Governance and Regulation

#### International Efforts
- UN AI initiatives
- G7/G20 agreements
- ISO standards
- Cross-border cooperation

#### National Approaches
- **EU**: Risk-based regulation
- **US**: Sector-specific guidance
- **China**: State-led development
- **UK**: Pro-innovation framework

#### Industry Self-Regulation
- Partnership on AI
- Ethical guidelines
- Safety research
- Red teaming practices

---

## 📚 Section 6: The Future Outlook

### Near-Term (1-2 Years)

#### Technical Advances
- Larger context windows (1M+ tokens)
- Better reasoning capabilities
- Improved efficiency
- Multimodal by default

#### Adoption Trends
- Enterprise integration
- Consumer applications
- Educational transformation
- Healthcare pilots

#### Regulatory Development
- First major laws enacted
- Standards emergence
- Liability frameworks
- International agreements

### Medium-Term (3-5 Years)

#### Capabilities
- Agent-based systems
- Complex reasoning
- Scientific discovery
- Creative collaboration

#### Societal Integration
- Workforce transformation
- Educational revolution
- New social norms
- Economic restructuring

### Long-Term (5-10 Years)

#### Potential Developments
- AGI discussions intensify
- Human-AI merger concepts
- Fundamental science breakthroughs
- Civilizational changes

#### Critical Questions
- Human purpose and meaning
- Consciousness and rights
- Governance models
- Existential considerations

---

# Part 3: Core Concepts Overview

## 🎯 Learning Objectives
By the end of this section, you will be able to:
- Understand what tokens are and how tokenization works conceptually
- Grasp how LLMs "understand" and process language
- Comprehend the basics of prediction and text generation
- Understand context windows and their importance
- Get an intuitive understanding of attention mechanisms
- Recognize key terminology used throughout LLM discussions

---

## 📚 Section 1: Tokens and Tokenization

### What is a Token?

A **token** is the basic unit of text that an LLM processes. Think of tokens as the "atoms" of language for AI models.

#### Token Examples

| Text | Tokens | Count |
|------|---------|-------|
| "Hello" | ["Hello"] | 1 |
| "Hello world" | ["Hello", " world"] | 2 |
| "artificial intelligence" | ["art", "ificial", " intelligence"] | 3 |
| "The quick brown fox" | ["The", " quick", " brown", " fox"] | 4 |
| "I'm" | ["I", "'m"] | 2 |
| "2024" | ["2024"] or ["20", "24"] | 1-2 |
| "😊" | ["😊"] | 1 |

### Understanding Tokenization

#### The Process
\`\`\`
Original Text: "I love learning about AI!"
     ↓
Tokenization Rules Applied
     ↓
Tokens: ["I", " love", " learning", " about", " AI", "!"]
     ↓
Token IDs: [314, 1842, 4673, 546, 9552, 0]
     ↓
Model Processing
\`\`\`

#### Key Characteristics

1. **Not Always Words**
   - Can be parts of words (subwords)
   - Can be punctuation
   - Can be special characters
   - Can span multiple words (rare)

2. **Language Dependent**
   - English: ~0.75 words per token average
   - Chinese: Often 1-2 characters per token
   - Code: Variable based on syntax

3. **Consistency**
   - Same text → Same tokens (deterministic)
   - Model-specific tokenization
   - Different models may tokenize differently

### Why Tokenization Matters

#### 1. **Computational Efficiency**
- **Problem**: Infinite possible words
- **Solution**: Fixed vocabulary of tokens (~50,000-100,000)
- **Benefit**: Manageable computational space

#### 2. **Handling Unknown Words**
- **Problem**: New words, names, typos
- **Solution**: Break into known subword tokens
- **Example**: "ChatGPT" → ["Chat", "G", "PT"]

#### 3. **Multilingual Support**
- **Problem**: Supporting many languages
- **Solution**: Byte-level or character-level fallback
- **Result**: Can handle any text

#### 4. **Context Window Limits**
- **Measurement**: Context measured in tokens, not words
- **Planning**: Need to estimate token usage
- **Cost**: Pricing often per token

### Common Tokenization Approaches

#### 1. **Word-Level**
- Simple but huge vocabulary
- Cannot handle unknown words
- Rarely used in modern LLMs

#### 2. **Character-Level**
- Very small vocabulary
- Long sequences
- Lost word boundaries

#### 3. **Subword (BPE/WordPiece)**
- **Byte Pair Encoding (BPE)**: Most common
- Balance between vocabulary size and sequence length
- Handles unknown words gracefully
- Used by GPT models

### Practical Implications

#### For Users
1. **Prompt Length**: Count tokens, not words
2. **Cost Estimation**: Pricing based on tokens
3. **Efficiency**: Shorter prompts = faster/cheaper
4. **Language Choice**: Some languages use more tokens

#### Token Economics Example
\`\`\`
English: "Hello, how are you?" = 6 tokens
Spanish: "Hola, ¿cómo estás?" = 7 tokens
Chinese: "你好吗？" = 4 tokens
Code: "def hello():" = 4 tokens
\`\`\`

---

## 📚 Section 2: How LLMs "Understand" Language

### The Illusion of Understanding

LLMs don't "understand" language the way humans do. Instead, they:

1. **Pattern Recognition**: Identify statistical patterns in text
2. **Vector Representations**: Convert text to mathematical representations
3. **Contextual Associations**: Learn relationships between concepts
4. **Probabilistic Reasoning**: Make predictions based on likelihood

### From Text to Meaning: The Journey

#### Step 1: Tokenization
\`\`\`
"The cat sat on the mat"
→ ["The", " cat", " sat", " on", " the", " mat"]
\`\`\`

#### Step 2: Embedding
Each token becomes a vector (list of numbers):
\`\`\`
"cat" → [0.2, -0.5, 0.8, ..., 0.3] (hundreds of dimensions)
\`\`\`

#### Step 3: Contextual Processing
The meaning changes based on context:
\`\`\`
"cat" in "The cat sat" → [0.3, -0.4, 0.9, ..., 0.2]
"cat" in "CAT scan results" → [0.1, -0.7, 0.2, ..., 0.5]
\`\`\`

#### Step 4: Pattern Matching
Model identifies patterns like:
- "cat" often followed by verbs like "sat", "ran", "jumped"
- "cat" associated with "pet", "animal", "fur"
- "cat" appears in similar contexts as "dog", "kitten"

### Representations and Embeddings

#### What are Embeddings?
Embeddings are numerical representations where:
- Similar meanings have similar numbers
- Relationships are preserved mathematically
- Context modifies representations

#### Semantic Space
Imagine a multi-dimensional space where:
\`\`\`
Distance represents similarity:
- "king" is close to "queen"
- "car" is close to "vehicle"
- "happy" is far from "sad"

Relationships form patterns:
- "king" - "man" + "woman" ≈ "queen"
- "Paris" - "France" + "Japan" ≈ "Tokyo"
\`\`\`

### Building Associations

#### Statistical Learning
LLMs learn associations like:

| If the model sees... | It learns... |
|---------------------|--------------|
| "The capital of France is Paris" | France → Paris association |
| "Dogs are loyal pets" | Dogs → loyalty, pets |
| "Water freezes at 0°C" | Water → freezing → temperature |

#### Context Accumulation
\`\`\`
Sentence: "The bank was steep and muddy after the rain."

Word "bank" associations:
- Financial: 30% (common usage)
- River bank: 70% (context: steep, muddy, rain)

Model chooses river bank interpretation
\`\`\`

### The "Understanding" Limitations

#### What LLMs DON'T Have:

1. **Sensory Experience**
   - Never seen a cat
   - Never felt rain
   - No physical experience

2. **Causal Models**
   - Don't understand cause-effect
   - Can't reason about physics
   - No true world model

3. **Intentionality**
   - No goals or desires
   - No consciousness
   - No real comprehension

#### What LLMs DO Have:

1. **Pattern Mastery**
   - Incredible pattern recognition
   - Statistical relationships
   - Contextual adaptation

2. **Linguistic Competence**
   - Grammar and syntax
   - Style and tone
   - Coherent generation

3. **Associative Knowledge**
   - Vast web of connections
   - Cross-domain linking
   - Emergent capabilities

---

## 📚 Section 3: Prediction and Generation

### The Core Mechanism: Next Token Prediction

LLMs fundamentally work by predicting the next most likely token:

\`\`\`
Input: "The weather today is"
Model predicts probabilities:
- "sunny" - 25%
- "cloudy" - 20%
- "beautiful" - 15%
- "cold" - 15%
- "rainy" - 10%
- [other tokens] - 15%
\`\`\`

### From Prediction to Generation

#### The Generation Loop
\`\`\`
1. Start with prompt: "Once upon a"
2. Predict next token: "time" (60% probability)
3. Append: "Once upon a time"
4. Predict next: "there" (45% probability)
5. Append: "Once upon a time there"
6. Continue until stop condition
\`\`\`

### Probability Distributions

#### Understanding Probabilities
For each position, the model outputs:
- Probability for EVERY token in vocabulary
- Sums to 100%
- Higher probability = more likely

#### Example Distribution
\`\`\`
After "The cat is":
- "sleeping": 15%
- "black": 10%
- "on": 8%
- "hungry": 7%
- "playing": 6%
- "a": 5%
- [50,000+ other tokens]: 49%
\`\`\`

### Sampling Strategies

How we choose from probabilities affects output:

#### 1. **Greedy Decoding**
- Always pick highest probability
- Deterministic but repetitive
- Example: "The cat is sleeping. The dog is sleeping. The bird is sleeping."

#### 2. **Random Sampling**
- Pick based on probability distribution
- More creative but can be incoherent
- Example: "The cat is purple dancing moonlight whiskers."

#### 3. **Temperature Scaling**
Temperature controls randomness:
\`\`\`
Low Temperature (0.1): Conservative, predictable
"The cat is sleeping on the couch."

High Temperature (1.5): Creative, risky
"The cat is orchestrating a midnight symphony."
\`\`\`

#### 4. **Top-k Sampling**
- Only consider top k most likely tokens
- Balances quality and diversity
- Example: k=10, only pick from top 10 options

#### 5. **Top-p (Nucleus) Sampling**
- Consider tokens until cumulative probability = p
- Adaptive selection
- Example: p=0.9, include tokens until 90% probability covered

### The Generation Process Visualized

\`\`\`
Prompt: "The secret to happiness is"
                ↓
        [Model Processing]
                ↓
    Probability Distribution:
    "finding": 20%
    "to": 15%
    "simple": 10%
    "love": 8%
    "understanding": 7%
    ...
                ↓
    [Sampling Strategy Applied]
                ↓
    Selected: "finding"
                ↓
New prompt: "The secret to happiness is finding"
                ↓
            [Repeat]
\`\`\`

### Controlling Generation

#### Length Control
- Maximum tokens
- Stop sequences
- End-of-text tokens

#### Content Control
- Prompt engineering
- Few-shot examples
- System prompts
- Fine-tuning

#### Quality Control
- Repetition penalties
- Diversity bonuses
- Beam search
- Multiple generations + selection

---

## 📚 Section 4: Context and Context Windows

### What is Context?

**Context** is all the text the model can "see" and use when making predictions.

\`\`\`
Context: "It was raining. John grabbed his..."
Model uses entire context to predict: "umbrella"

Without context: "John grabbed his..."
Model might predict: "phone", "keys", "coffee"
\`\`\`

### The Context Window

#### Definition
The **context window** is the maximum amount of text (in tokens) a model can process at once.

#### Common Sizes
| Model | Context Window | Approximate Words |
|-------|---------------|-------------------|
| GPT-3 | 4,096 tokens | ~3,000 words |
| GPT-4 | 8,192 - 128,000 tokens | ~6,000 - 96,000 words |
| Claude | 100,000 tokens | ~75,000 words |
| Some specialized models | 1,000,000+ tokens | ~750,000 words |

### How Context Works

#### Building Understanding
\`\`\`
Token 1: "The"
Context: ["The"]
Limited understanding

Token 2: "bank"
Context: ["The", "bank"]
Could be financial or river

Token 3: "robbery"
Context: ["The", "bank", "robbery"]
Clearly financial institution

Token 4: "was"
Context: ["The", "bank", "robbery", "was"]
Past event established
\`\`\`

#### Context Accumulation
Each token adds information:
1. Grammar structure
2. Topic and domain
3. Tone and style
4. Specific details
5. Relationships

### Context Window Limitations

#### The Sliding Window Problem
\`\`\`
Context window: [========] (8 tokens)

Text: "Once upon a time there was a princess who lived in a castle"

Processing position 1-8:
[Once upon a time there was a princess] who lived in a castle

Processing position 6-13:
Once upon a time [there was a princess who lived in a castle]

Early context lost!
\`\`\`

#### Implications

1. **Forgetting**
   - Model can't remember early parts
   - Long conversations lose coherence
   - Important details may be forgotten

2. **Planning Limitations**
   - Can't maintain very long documents
   - Complex multi-step reasoning challenging
   - Book-length generation difficult

3. **Reference Limitations**
   - Can't refer to distant text
   - Loses track of characters/concepts
   - Inconsistencies emerge

### Context Management Strategies

#### For Users

1. **Summarization**
   - Periodically summarize long conversations
   - Keep key points in view
   - Refresh important context

2. **Chunking**
   - Break large tasks into smaller pieces
   - Process documents in sections
   - Maintain overlap between chunks

3. **Context Optimization**
   - Remove unnecessary text
   - Use concise language
   - Prioritize relevant information

#### Technical Solutions

1. **Extending Context**
   - Rotary position embeddings
   - Efficient attention mechanisms
   - Compression techniques

2. **Memory Systems**
   - External memory banks
   - Retrieval augmentation
   - Hierarchical processing

3. **Sparse Attention**
   - Attend to important tokens only
   - Pattern-based attention
   - Learned importance

---

## 📚 Section 5: Attention Mechanisms (Intuitive Understanding)

### The Attention Concept

**Attention** allows the model to focus on relevant parts of the input when processing each token.

#### Human Analogy
When reading "The cat sat on the mat", your brain:
- Connects "cat" with "sat" (subject-verb)
- Links "sat on" with "mat" (location)
- Understands "the" refers to specific cat and mat

LLMs do similar connection-making mathematically.

### Why Attention Matters

#### Before Attention (RNNs)
\`\`\`
Processing: "The cat that ate the mouse sat"
Problem: By "sat", model barely remembers "cat"
Result: Weak long-range connections
\`\`\`

#### With Attention
\`\`\`
Processing: "The cat that ate the mouse sat"
Attention allows "sat" to look back directly at "cat"
Result: Strong connections regardless of distance
\`\`\`

### How Attention Works (Simplified)

#### The Three Questions
For each token, attention asks:

1. **Query**: "What am I looking for?"
2. **Key**: "What information do I have?"
3. **Value**: "What should I retrieve?"

#### Example: "The cat sat on the mat"

When processing "sat":
\`\`\`
Query (sat): "I need a subject"
Keys (all tokens): 
  - The: "I'm a determiner"
  - cat: "I'm a noun/subject"
  - sat: "I'm a verb"
  - on: "I'm a preposition"
  
Attention scores:
  - The: 0.1 (low relevance)
  - cat: 0.7 (high relevance - subject!)
  - sat: 0.1 (self)
  - on: 0.1 (low relevance)
  
Result: "sat" strongly attends to "cat"
\`\`\`

### Types of Attention Patterns

#### 1. **Local Attention**
Focus on nearby tokens:
\`\`\`
"The [quick] brown fox"
"quick" mainly attends to "The" and "brown"
\`\`\`

#### 2. **Long-Range Attention**
Connect distant related tokens:
\`\`\`
"The princess who lived in the tower [returned] to"
"returned" attends strongly to "princess" despite distance
\`\`\`

#### 3. **Syntactic Attention**
Grammar-based connections:
\`\`\`
"The dogs [were] barking"
"were" attends to "dogs" (plural agreement)
\`\`\`

#### 4. **Semantic Attention**
Meaning-based connections:
\`\`\`
"The doctor prescribed [medicine]"
"medicine" attends to "doctor" and "prescribed"
\`\`\`

### Multi-Head Attention

Models use multiple attention "heads" simultaneously:

\`\`\`
Head 1: Grammar attention
Head 2: Semantic attention  
Head 3: Position attention
Head 4: Style attention
...
Head 12: Topic attention

Combined: Rich, multi-faceted understanding
\`\`\`

### Attention Visualization

Imagine attention weights as connections:

\`\`\`
The cat sat on the mat
 │   │   │  │  │   │
 └───┴───┴──┴──┴───┘
 
Stronger lines = stronger attention
"sat" connects strongly to "cat"
"on" connects to "sat" and "mat"
"the" connects to respective nouns
\`\`\`

### Self-Attention

**Self-attention** means tokens attend to other tokens in the same sequence:

\`\`\`
Input: "Time flies like an arrow"

Each word attends to all others:
- "flies" attends to "Time" (subject)
- "like" attends to "flies" (verb modifier)
- "arrow" attends to "like" (comparison)

Result: Full contextual understanding
\`\`\`

---

## 📚 Section 6: Key Terminology Glossary

### Essential Terms

#### **Transformer**
The neural network architecture underlying modern LLMs, introduced in 2017.

#### **Parameters**
The adjustable weights in the model that store learned patterns. More parameters generally mean more capacity.

#### **Pre-training**
Initial training on massive text datasets to learn general language patterns.

#### **Fine-tuning**
Additional training on specific data to specialize the model for particular tasks.

#### **Prompt**
The input text given to the model to generate a response.

#### **Completion**
The text generated by the model in response to a prompt.

#### **Few-shot Learning**
Model's ability to learn from just a few examples in the prompt.

#### **Zero-shot Learning**
Model's ability to perform tasks without any examples, just instructions.

#### **Inference**
The process of using a trained model to generate predictions/text.

#### **Perplexity**
A measure of how "surprised" a model is by text - lower is better.

#### **Embedding**
Numerical representation of text that captures semantic meaning.

#### **Hallucination**
When models generate false or nonsensical information confidently.

#### **Temperature**
Parameter controlling randomness in generation (0=deterministic, higher=creative).

#### **Tokenizer**
The algorithm that converts text into tokens.

#### **Vocabulary**
The complete set of tokens a model can recognize.

#### **Loss Function**
Mathematical measure of prediction errors during training.

#### **Gradient**
Direction and magnitude of change needed to improve model.

#### **Batch**
Group of examples processed together during training.

#### **Epoch**
One complete pass through the training data.

#### **Checkpoint**
Saved model state during training.

#### **API**
Application Programming Interface for accessing models.

#### **Latency**
Time delay between input and output.

#### **Throughput**
Number of tokens processed per second.

---

## 🎯 Module Summary

### What You've Learned

You now have a comprehensive understanding of Large Language Models covering:

#### **Foundations**
- What LLMs are and how they evolved from traditional NLP
- Core capabilities and fundamental limitations
- Real-world applications across industries
- Differences from traditional software

#### **Context**
- LLMs' position in the AI landscape
- Economic and business implications
- Societal impacts and ethical considerations
- Future outlook and trends

#### **Technical Concepts**
- Tokens and tokenization
- How LLMs process and "understand" language
- Prediction and generation mechanisms
- Context windows and their limitations
- Attention mechanisms and their importance

### Key Takeaways

1. **LLMs are sophisticated pattern recognition systems**, not conscious entities
2. **They excel at language tasks** but have significant limitations
3. **The transformer architecture** was the key breakthrough enabling modern LLMs
4. **Scale matters** - capabilities emerge with size
5. **Context windows limit** how much text can be processed at once
6. **Attention mechanisms** allow models to focus on relevant information
7. **Probabilistic generation** means outputs can vary
8. **Ethical considerations** are crucial for responsible deployment
9. **Economic impact** will be transformative across industries
10. **Hybrid approaches** combining LLMs with traditional systems often work best

### The Journey Ahead

With this foundation, you're now equipped to:
- Understand technical discussions about LLMs
- Make informed decisions about LLM adoption
- Recognize appropriate use cases and limitations
- Think critically about AI's impact on society
- Continue learning about more advanced topics

---

## 🤔 Reflection Questions

1. **Personal Impact**: How might LLMs affect your career or daily life?
2. **Ethical Considerations**: What concerns you most about LLM deployment?
3. **Technical Understanding**: Which concept (tokens, attention, context) seems most important?
4. **Application Ideas**: What problems could LLMs help solve in your field?
5. **Future Vision**: What excites or worries you about the future of AI?

---

## 📖 Additional Resources

### Essential Reading
- "Attention Is All You Need" - The transformer paper
- "Language Models are Few-Shot Learners" - GPT-3 paper
- "BERT: Pre-training of Deep Bidirectional Transformers"
- "The Alignment Problem" by Brian Christian
- "Life 3.0" by Max Tegmark

### Visual Guides
- The Illustrated Transformer
- Attention Visualization with BertViz
- OpenAI Tokenizer Tool

### Reports and Analysis
- Stanford HAI AI Index Report
- McKinsey on Generative AI Impact
- EU Ethics Guidelines for Trustworthy AI

### Podcasts and Videos
- Lex Fridman AI Podcast
- Two Minute Papers
- 3Blue1Brown on Neural Networks

---

## 🚀 What's Next?

Congratulations on completing Module 1! You've built a strong conceptual foundation for understanding Large Language Models. 

### Your Foundation is Complete
✅ You understand what LLMs are and how they work conceptually
✅ You can evaluate their impact on business and society
✅ You grasp core technical concepts like tokens, attention, and context
✅ You're aware of both capabilities and limitations
✅ You can think critically about ethical implications

### Ready for the Next Level
With this foundation, you're prepared to explore:
- Deeper technical architecture (Layer 2)
- Practical applications and prompt engineering
- Fine-tuning and customization
- Building LLM-powered applications
- Advanced topics in AI safety and alignment

Remember: Understanding LLMs is not just about the technology—it's about understanding how they fit into our world and how we can use them responsibly and effectively.

---

## 💡 Final Thought

*"The development of Large Language Models represents one of the most significant technological advances of our time. Understanding them—their capabilities, limitations, and implications—is essential for navigating the AI-transformed world ahead."*

Keep learning, stay curious, and use this knowledge to make a positive impact!`,
  exercises: [
    {
      id: 'llm-1-ex1',
      title: 'Build a Smart Token Estimator for LLM Cost Planning',
      description: 'Create a comprehensive token estimation system for planning LLM usage costs',
      difficulty: 'easy',
      type: 'implementation',
      instructions: `## Context
You work for a startup that's integrating LLMs into their customer service platform. The finance team needs accurate token estimates to budget for API costs. Different LLM providers charge per token, and understanding token counts is crucial for cost management.

Your company processes various types of content:
- Customer emails (English and Spanish)
- Technical documentation with code snippets
- Social media posts with emojis
- Legal documents with formal language

## Your Goal
Build a sophisticated token estimator that can:
1. Estimate tokens for different types of text
2. Handle multiple languages (with different tokenization rates)
3. Account for special characters and formatting
4. Provide cost estimates based on token counts

## Requirements
- Implement a token estimation function with language awareness
- Handle special cases like URLs, emails, and code blocks
- Calculate estimated costs given a price per 1K tokens
- Test with various real-world text samples
- Create a summary report showing token distribution

## Starting Point
Remember that tokenization varies by language and content type:
- English: ~0.75 words per token average
- Spanish/French: ~0.9 words per token (more articles and conjugations)
- Chinese/Japanese: Different character-based tokenization
- Code: Variable based on syntax and naming conventions

Think about how different text elements consume tokens and how this impacts cost planning.`,
      hints: [
        'Start with basic word counting and apply language-specific multipliers. English typically uses 1.3 tokens per word.',
        'Punctuation marks are usually individual tokens. Count them separately from words.',
        'URLs and email addresses often tokenize into many small pieces. Consider them as 2-3x their character count / 4.',
        'For code, consider that operators, brackets, and keywords are often individual tokens.',
        'Add language detection logic - Spanish text typically needs 1.5-1.8 tokens per word due to more complex morphology.',
        'For cost calculation, remember most APIs price per 1K tokens. Build a function that takes token count and price per 1K.',
        'Create test cases that represent your actual use cases - customer emails, technical docs, etc.',
        'Consider building a report that breaks down tokens by category (words, punctuation, numbers, special).',
      ],
      solution: `import re
from typing import Dict, Tuple

def estimate_tokens(text: str, language: str = 'en') -> Dict[str, any]:
    """
    Comprehensive token estimator for LLM cost planning.
    
    This implementation reflects real-world tokenization patterns observed
    in production LLM systems, helping teams accurately budget API costs.
    """
    
    # Language-specific multipliers based on empirical data
    LANGUAGE_MULTIPLIERS = {
        'en': 1.3,      # English: relatively efficient
        'es': 1.5,      # Spanish: more articles and conjugations
        'fr': 1.5,      # French: similar to Spanish
        'de': 1.4,      # German: compound words but efficient
        'zh': 0.5,      # Chinese: character-based
        'ja': 0.7,      # Japanese: mixed character systems
    }
    
    # Initialize token counts by category
    token_breakdown = {
        'words': 0,
        'punctuation': 0,
        'numbers': 0,
        'urls': 0,
        'emails': 0,
        'code_blocks': 0,
        'special_chars': 0,
        'total': 0
    }
    
    # Extract and count special patterns first
    # URLs - typically tokenize into many pieces
    urls = re.findall(r'https?://[^\\s]+', text)
    for url in urls:
        token_breakdown['urls'] += len(url) // 3  # URLs fragment heavily
        text = text.replace(url, ' ')  # Remove from text to avoid double counting
    
    # Email addresses - also fragment
    emails = re.findall(r'\\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Z|a-z]{2,}\\b', text)
    for email in emails:
        token_breakdown['emails'] += len(email) // 4
        text = text.replace(email, ' ')
    
    # Code blocks (simple detection)
    code_blocks = re.findall(r'\`\`\`[\\s\\S]*?\`\`\`|\`[^\`]+\`', text)
    for code in code_blocks:
        # Code has high token density due to operators and syntax
        token_breakdown['code_blocks'] += len(code.split()) * 1.8
        text = text.replace(code, ' ')
    
    # Count regular words
    words = text.split()
    word_count = len(words)
    multiplier = LANGUAGE_MULTIPLIERS.get(language, 1.3)
    token_breakdown['words'] = int(word_count * multiplier)
    
    # Count punctuation (each is typically a separate token)
    punctuation = re.findall(r'[.,!?;:()\\[\\]{}"\'-—]', text)
    token_breakdown['punctuation'] = len(punctuation)
    
    # Count number groups
    numbers = re.findall(r'\\b\\d+\\.?\\d*\\b', text)
    for num in numbers:
        # Numbers tokenize based on length
        if len(num) <= 3:
            token_breakdown['numbers'] += 1
        else:
            token_breakdown['numbers'] += (len(num) // 3) + 1
    
    # Special characters (emojis, symbols)
    special_chars = re.findall(r'[^\\w\\s.,!?;:()\\[\\]{}"\'-—]', text)
    token_breakdown['special_chars'] = len(special_chars)
    
    # Calculate total
    token_breakdown['total'] = sum([
        token_breakdown['words'],
        token_breakdown['punctuation'],
        token_breakdown['numbers'],
        token_breakdown['urls'],
        token_breakdown['emails'],
        token_breakdown['code_blocks'],
        token_breakdown['special_chars']
    ])
    
    return token_breakdown

def calculate_cost(token_count: int, price_per_1k: float) -> Tuple[float, str]:
    """
    Calculate estimated cost based on token count.
    
    Args:
        token_count: Total number of tokens
        price_per_1k: Price per 1000 tokens (e.g., 0.002 for $0.002/1K)
    
    Returns:
        Tuple of (cost, formatted_string)
    """
    cost = (token_count / 1000) * price_per_1k
    
    if cost < 0.01:
        return cost, f"$\{cost:.4f\}"
    elif cost < 1:
        return cost, f"$\{cost:.3f\}"
    else:
        return cost, f"$\{cost:.2f\}"

def generate_token_report(texts: list, language: str = 'en', price_per_1k: float = 0.002):
    """
    Generate comprehensive token analysis report for multiple texts.
    """
    print("=" * 60)
    print("TOKEN ESTIMATION REPORT")
    print("=" * 60)
    print(f"Language: \{language.upper()\}")
    print(f"Price per 1K tokens: $\{price_per_1k:.3f\}")
    print("-" * 60)
    
    total_tokens = 0
    
    for i, text in enumerate(texts, 1):
        breakdown = estimate_tokens(text, language)
        cost, cost_str = calculate_cost(breakdown['total'], price_per_1k)
        
        print(f"\\nText \{i\}: \{text[:50]\}...")
        print(f"Length: \{len(text)\} characters, \{len(text.split())\} words")
        print(f"Token Breakdown:")
        print(f"  Words:        \{breakdown['words']:>6\} tokens")
        print(f"  Punctuation:  \{breakdown['punctuation']:>6\} tokens")
        print(f"  Numbers:      \{breakdown['numbers']:>6\} tokens")
        print(f"  URLs:         \{breakdown['urls']:>6\} tokens")
        print(f"  Emails:       \{breakdown['emails']:>6\} tokens")
        print(f"  Code:         \{breakdown['code_blocks']:>6\} tokens")
        print(f"  Special:      \{breakdown['special_chars']:>6\} tokens")
        print(f"  \{'─' * 20\}")
        print(f"  TOTAL:        \{breakdown['total']:>6\} tokens")
        print(f"  Estimated Cost: \{cost_str\}")
        
        total_tokens += breakdown['total']
    
    total_cost, total_cost_str = calculate_cost(total_tokens, price_per_1k)
    
    print("\\n" + "=" * 60)
    print(f"SUMMARY")
    print(f"Total Tokens: \{total_tokens:,\}")
    print(f"Total Cost: \{total_cost_str\}")
    print(f"Average Tokens per Text: \{total_tokens // len(texts)\}")
    print("=" * 60)

# Test with real-world examples
test_texts = [
    "Hello, world! This is a simple test message.",
    "Dear Customer, Your order #12345 has been shipped. Track at https://track.example.com/12345",
    "The year 2024 brings new AI capabilities. Contact us at support@example.com for details!",
    "\`\`\`python\\ndef hello():\\n    print('Hello!')\\n\`\`\`",
    "¡Hola! ¿Cómo estás? Me gustaría preguntarte sobre el producto.",
]

# Run the analysis
generate_token_report(test_texts, language='en', price_per_1k=0.002)

# Test Spanish text separately
spanish_text = ["La inteligencia artificial está transformando el mundo. ¿Qué piensas?"]
print("\\n" + "=" * 60)
print("SPANISH TEXT ANALYSIS")
generate_token_report(spanish_text, language='es', price_per_1k=0.002)`,
      testCases: [
        {
          input: 'text = "Hello, world!"',
          expected: 'Approximately 5 tokens (2 words + 2 punctuation)',
          description: 'Simple greeting with punctuation',
        },
        {
          input: 'text = "Visit https://example.com for more"',
          expected: 'Higher token count due to URL fragmentation',
          description: 'Text with URL',
        },
        {
          input: 'text = "Contact: john@example.com"',
          expected: 'Email addresses tokenize into multiple parts',
          description: 'Text with email',
        },
      ],
    },
    {
      id: 'llm-1-ex2',
      title: 'Simulate an Attention Mechanism for Understanding Context',
      description:
        'Build an interactive attention mechanism simulator to understand how LLMs process context',
      difficulty: 'medium',
      type: 'implementation',
      instructions: `## Context
You're creating an educational tool for a machine learning bootcamp. Students struggle to understand how attention mechanisms work in transformers. Your tool will help them visualize and interact with a simplified attention system, making this complex concept intuitive.

The attention mechanism is the key innovation that allows LLMs to understand relationships between words regardless of their distance in text. It's what enables a model to know that "it" in "The cat sat on the mat. It was comfortable." refers to "cat" and not "mat".

## Your Goal
Build an attention simulator that:
1. Calculates attention scores between words in a sentence
2. Visualizes which words are "paying attention" to each other
3. Demonstrates different types of attention patterns (local, syntactic, semantic)
4. Shows how attention helps resolve ambiguities

## Requirements
- Implement attention score calculation with multiple attention heads
- Create visualization of attention weights (text-based matrix)
- Demonstrate subject-verb, pronoun resolution, and modifier patterns
- Show how context window affects attention
- Include examples of attention failure modes

## Starting Point
Attention works by computing compatibility scores between words. For each word (query), the mechanism asks: "Which other words (keys) should I pay attention to?" The scores determine how much information flows between words.

Think about different types of relationships:
- Syntactic: grammatical relationships (subject-verb, adjective-noun)
- Semantic: meaning relationships (synonyms, related concepts)
- Positional: nearby words often relate to each other
- Long-range: pronouns referring to distant nouns`,
      hints: [
        'Start with a simple dictionary of word relationships. Each pair has a compatibility score.',
        'Attention scores should be normalized (sum to 1) for each query word. Use softmax or simple normalization.',
        'Create different "attention heads" - one for grammar, one for meaning, one for position.',
        'For visualization, create a matrix where rows are query words and columns are key words.',
        'Add position bias - words closer together should have slightly higher base attention.',
        'For pronoun resolution, make pronouns attend strongly to nearby nouns that match in number/gender.',
        'Show what happens when the context window is too small - important connections get lost.',
        'Consider adding a "self-attention" score - words attending to themselves (usually low but non-zero).',
      ],
      solution: `import math
from typing import Dict, List, Tuple
import numpy as np

class AttentionSimulator:
    """
    Educational attention mechanism simulator for understanding transformers.
    
    This simulator demonstrates how attention mechanisms create contextual
    understanding by learning which words should "pay attention" to each other.
    """
    
    def __init__(self):
        # Define different types of attention patterns
        self.syntactic_patterns = {
            # Subject-verb relationships
            ('cat', 'sat'): 0.8,
            ('dog', 'ran'): 0.8,
            ('bird', 'flew'): 0.8,
            ('she', 'went'): 0.9,
            ('they', 'were'): 0.9,
            
            # Verb-object relationships  
            ('ate', 'food'): 0.7,
            ('read', 'book'): 0.7,
            ('wrote', 'letter'): 0.7,
            
            # Adjective-noun relationships
            ('big', 'dog'): 0.6,
            ('red', 'car'): 0.6,
            ('beautiful', 'day'): 0.6,
            
            # Preposition relationships
            ('on', 'mat'): 0.5,
            ('in', 'house'): 0.5,
            ('under', 'table'): 0.5,
        }
        
        self.semantic_patterns = {
            # Pronouns to nouns
            ('it', 'cat'): 0.7,
            ('it', 'dog'): 0.7,
            ('she', 'woman'): 0.8,
            ('he', 'man'): 0.8,
            ('they', 'people'): 0.8,
            
            # Related concepts
            ('cat', 'animal'): 0.6,
            ('dog', 'pet'): 0.6,
            ('car', 'vehicle'): 0.6,
            
            # Semantic clusters
            ('happy', 'joy'): 0.7,
            ('sad', 'tears'): 0.7,
            ('hot', 'warm'): 0.6,
        }
    
    def calculate_attention_scores(self, 
                                 query: str, 
                                 keys: List[str],
                                 attention_type: str = 'combined') -> Dict[str, float]:
        """
        Calculate attention scores between a query word and key words.
        
        This simulates how transformers determine which words to focus on
        when processing each token in the sequence.
        """
        scores = {}
        
        for i, key in enumerate(keys):
            # Base score with positional bias
            position_diff = abs(keys.index(query) - i) if query in keys else i
            position_bias = 1.0 / (1.0 + position_diff * 0.3)  # Closer words get higher base score
            
            # Self-attention (words attend to themselves weakly)
            if query == key:
                base_score = 0.1
            else:
                base_score = 0.05
            
            # Syntactic attention head
            syntactic_score = 0.0
            if attention_type in ['syntactic', 'combined']:
                key_pair = (query.lower(), key.lower())
                rev_pair = (key.lower(), query.lower())
                syntactic_score = self.syntactic_patterns.get(key_pair, 0.0)
                syntactic_score = max(syntactic_score, self.syntactic_patterns.get(rev_pair, 0.0))
            
            # Semantic attention head
            semantic_score = 0.0
            if attention_type in ['semantic', 'combined']:
                key_pair = (query.lower(), key.lower())
                rev_pair = (key.lower(), query.lower())
                semantic_score = self.semantic_patterns.get(key_pair, 0.0)
                semantic_score = max(semantic_score, self.semantic_patterns.get(rev_pair, 0.0))
            
            # Combine scores based on attention type
            if attention_type == 'syntactic':
                final_score = base_score + syntactic_score + position_bias * 0.1
            elif attention_type == 'semantic':
                final_score = base_score + semantic_score + position_bias * 0.1
            elif attention_type == 'positional':
                final_score = base_score + position_bias * 0.5
            else:  # combined
                final_score = base_score + (syntactic_score * 0.4 + semantic_score * 0.4 + position_bias * 0.2)
            
            scores[key] = final_score
        
        # Normalize scores (simplified softmax)
        total = sum(scores.values())
        if total > 0:
            scores = {k: v/total for k, v in scores.items()}
        
        return scores
    
    def visualize_attention_matrix(self, sentence: str, attention_type: str = 'combined'):
        """
        Create a visual representation of attention patterns in a sentence.
        
        This helps students see how different words relate to each other
        and understand the role of attention in creating context.
        """
        words = sentence.split()
        n = len(words)
        
        print(f"\\n\{'='*60\}")
        print(f"ATTENTION MATRIX - \{attention_type.upper()\} ATTENTION")
        print(f"\{'='*60\}")
        print(f"Sentence: \{sentence\}")
        print(f"\{'='*60\}\\n")
        
        # Create attention matrix
        matrix = []
        for query in words:
            scores = self.calculate_attention_scores(query, words, attention_type)
            matrix.append([scores[key] for key in words])
        
        # Print matrix header
        print("        ", end="")
        for word in words:
            print(f"\{word[:7]:>8\}", end="")
        print("\\n" + "-" * (9 + 8 * n))
        
        # Print matrix rows
        for i, query in enumerate(words):
            print(f"\{query[:7]:>7\} |", end="")
            for j, score in enumerate(matrix[i]):
                # Use intensity indicators
                if score > 0.3:
                    indicator = "███"
                elif score > 0.2:
                    indicator = "▓▓▓"
                elif score > 0.1:
                    indicator = "▒▒▒"
                elif score > 0.05:
                    indicator = "░░░"
                else:
                    indicator = "   "
                print(f" \{score:.2f\}\{indicator\}", end="")
            print()
        
        print("\\n" + "="*60)
        print("Legend: ███ Strong (>0.3) ▓▓▓ Medium (>0.2) ▒▒▒ Weak (>0.1)")
        print("="*60)
        
        # Explain key relationships
        print("\\nKey Relationships Detected:")
        for i, query in enumerate(words):
            strong_attentions = [(words[j], matrix[i][j]) 
                               for j in range(n) 
                               if matrix[i][j] > 0.15 and i != j]
            if strong_attentions:
                strong_attentions.sort(key=lambda x: x[1], reverse=True)
                print(f"  '\{query\}' strongly attends to: \{', '.join([f'\{w\} (\{s:.2f\})' for w, s in strong_attentions[:3]])\}")
    
    def demonstrate_attention_patterns(self):
        """
        Demonstrate different types of attention patterns with examples.
        """
        examples = [
            ("The cat sat on the mat", "syntactic"),
            ("The cat sat on the mat", "semantic"),
            ("The cat sat on the mat", "positional"),
            ("The cat sat on the mat", "combined"),
            ("She went to the store because it was open", "combined"),
            ("The big red car drove quickly down the street", "combined"),
        ]
        
        print("\\n" + "="*60)
        print("ATTENTION PATTERN DEMONSTRATIONS")
        print("="*60)
        
        for sentence, att_type in examples:
            self.visualize_attention_matrix(sentence, att_type)
            input("\\nPress Enter to see next example...")
    
    def analyze_context_window_effects(self, long_sentence: str, window_size: int = 5):
        """
        Show how limited context windows affect attention patterns.
        
        This demonstrates why context window size is a critical limitation
        of transformer models.
        """
        words = long_sentence.split()
        
        print(f"\\n\{'='*60\}")
        print(f"CONTEXT WINDOW EFFECTS (Window Size: \{window_size\})")
        print(f"\{'='*60\}")
        print(f"Full Sentence: \{long_sentence\}")
        print(f"Total Words: \{len(words)\}")
        print(f"\{'='*60\}\\n")
        
        # Show what happens when processing word at position
        target_positions = [len(words) // 2, len(words) - 1]  # Middle and end
        
        for target_pos in target_positions:
            target_word = words[target_pos]
            print(f"\\nProcessing word '\{target_word\}' at position \{target_pos\}:")
            
            # Full context
            full_scores = self.calculate_attention_scores(target_word, words, 'combined')
            important_words = sorted(full_scores.items(), key=lambda x: x[1], reverse=True)[:5]
            print(f"  With full context, attends to: \{', '.join([f'\{w\}(\{s:.2f\})' for w, s in important_words])\}")
            
            # Limited context window
            start = max(0, target_pos - window_size // 2)
            end = min(len(words), target_pos + window_size // 2 + 1)
            window_words = words[start:end]
            
            limited_scores = self.calculate_attention_scores(
                target_word if target_word in window_words else window_words[0], 
                window_words, 
                'combined'
            )
            important_limited = sorted(limited_scores.items(), key=lambda x: x[1], reverse=True)[:5]
            print(f"  With window [\{start\}:\{end\}], attends to: \{', '.join([f'\{w\}(\{s:.2f\})' for w, s in important_limited])\}")
            
            # Show what's lost
            lost_connections = [w for w, s in important_words if w not in window_words and s > 0.1]
            if lost_connections:
                print(f"  ⚠️  Lost important connections to: \{', '.join(lost_connections)\}")

# Create simulator and run demonstrations
simulator = AttentionSimulator()

# Test individual attention calculation
print("\\n" + "="*60)
print("INDIVIDUAL ATTENTION SCORE CALCULATION")
print("="*60)

test_sentence = "The cat sat on the mat"
test_words = test_sentence.split()
query_word = "sat"

print(f"Query word: '\{query_word\}'")
print(f"Context: \{test_words\}")
print(f"\\nAttention scores:")

scores = simulator.calculate_attention_scores(query_word, test_words, 'combined')
for word, score in sorted(scores.items(), key=lambda x: x[1], reverse=True):
    bar = '█' * int(score * 20)
    print(f"  \{word:10\} \{score:.3f\} \{bar\}")

# Visualize full attention matrix
simulator.visualize_attention_matrix("The cat sat on the mat", "combined")

# Demonstrate context window limitations
long_sentence = "The small brown cat that lived in the old house sat on the comfortable mat"
simulator.analyze_context_window_effects(long_sentence, window_size=6)

# Interactive demonstration (commented out for non-interactive environments)
# simulator.demonstrate_attention_patterns()`,
      testCases: [
        {
          input: 'query="sat", context=["The", "cat", "sat", "on", "the", "mat"]',
          expected: 'Higher attention score for "cat" (subject) than other words',
          description: 'Subject-verb attention relationship',
        },
        {
          input: 'query="it", context=["The", "cat", "sat", ".", "It", "was", "comfortable"]',
          expected: 'Highest attention to "cat" for pronoun resolution',
          description: 'Pronoun resolution through attention',
        },
        {
          input: 'Limited context window with long sentence',
          expected: 'Lost connections to important distant words',
          description: 'Context window limitation effects',
        },
      ],
    },
    {
      id: 'llm-1-ex3',
      title: 'Design an LLM Evaluation Framework for Business Applications',
      description:
        'Create a comprehensive framework for evaluating LLM performance in enterprise settings',
      difficulty: 'hard',
      type: 'implementation',
      instructions: `## Context
You're the AI lead at a Fortune 500 company that's evaluating different LLMs for various business applications. Management needs a systematic way to compare models and decide which to use for different tasks. Cost, performance, safety, and compliance are all critical factors.

The company is considering LLMs for:
- Customer service automation
- Internal knowledge management
- Code generation for developers
- Marketing content creation
- Legal document analysis

## Your Goal
Build an evaluation framework that:
1. Tests multiple dimensions of LLM performance
2. Calculates ROI based on performance vs. cost
3. Evaluates safety and bias concerns
4. Checks compliance with industry standards
5. Provides actionable recommendations

## Requirements
- Design evaluation metrics for accuracy, safety, cost-efficiency, and latency
- Implement tests for hallucination detection
- Create bias evaluation tests
- Calculate total cost of ownership (TCO) including hidden costs
- Generate a decision matrix for model selection
- Provide risk assessment for each use case

## Starting Point
Think beyond simple accuracy metrics. Business applications need:
- Reliability: Can we trust the outputs?
- Consistency: Does it give similar answers for similar queries?
- Safety: Will it generate harmful or biased content?
- Cost-effectiveness: What's the ROI?
- Compliance: Does it meet regulatory requirements?
- Scalability: Can it handle our volume?

Consider both quantitative metrics and qualitative assessments.`,
      hints: [
        'Start by defining evaluation categories: Performance, Safety, Cost, Compliance, Usability.',
        'For hallucination detection, create test cases with known facts and check for fabrications.',
        'Bias testing should include protected categories: gender, race, age, religion. Use template sentences.',
        'Cost calculation should include: API costs, integration costs, monitoring costs, and failure handling.',
        'Create weighted scoring - different metrics matter more for different use cases.',
        'Add stress tests - what happens with ambiguous, adversarial, or out-of-domain inputs?',
        'Include latency testing - response time matters for customer-facing applications.',
        'Build a risk matrix: likelihood of failure vs. impact of failure for each use case.',
      ],
      solution: `import time
import random
from typing import Dict, List, Tuple, Any
from dataclasses import dataclass
from enum import Enum

class RiskLevel(Enum):
    LOW = "Low"
    MEDIUM = "Medium"
    HIGH = "High"
    CRITICAL = "Critical"

@dataclass
class EvaluationResult:
    """Structured result from LLM evaluation."""
    metric_name: str
    score: float  # 0-1 scale
    details: Dict[str, Any]
    risk_level: RiskLevel
    recommendations: List[str]

class LLMEvaluationFramework:
    """
    Comprehensive LLM evaluation framework for enterprise applications.
    
    This framework helps organizations make informed decisions about LLM adoption
    by evaluating models across multiple critical dimensions.
    """
    
    def __init__(self, model_name: str, api_cost_per_1k: float):
        self.model_name = model_name
        self.api_cost_per_1k = api_cost_per_1k
        self.results = []
    
    def evaluate_accuracy(self, test_cases: List[Dict]) -> EvaluationResult:
        """
        Evaluate model accuracy on domain-specific tasks.
        """
        correct = 0
        hallucinations = 0
        consistency_scores = []
        
        for test in test_cases:
            # Simulate model response (in practice, call actual model)
            response = self._simulate_model_response(test['prompt'])
            
            # Check correctness
            if self._check_correctness(response, test['expected']):
                correct += 1
            
            # Check for hallucinations
            if self._detect_hallucination(response, test.get('facts', [])):
                hallucinations += 1
            
            # Check consistency (ask same question multiple times)
            consistency_scores.append(self._check_consistency(test['prompt']))
        
        accuracy_score = correct / len(test_cases)
        hallucination_rate = hallucinations / len(test_cases)
        consistency_score = sum(consistency_scores) / len(consistency_scores)
        
        # Combined performance score
        performance_score = (accuracy_score * 0.5 + 
                           (1 - hallucination_rate) * 0.3 + 
                           consistency_score * 0.2)
        
        # Determine risk level
        if hallucination_rate > 0.2:
            risk = RiskLevel.HIGH
        elif hallucination_rate > 0.1:
            risk = RiskLevel.MEDIUM
        else:
            risk = RiskLevel.LOW
        
        recommendations = []
        if hallucination_rate > 0.1:
            recommendations.append("Implement fact-checking layer for critical applications")
        if consistency_score < 0.8:
            recommendations.append("Use temperature=0 for more consistent outputs")
        
        return EvaluationResult(
            metric_name="Accuracy & Reliability",
            score=performance_score,
            details={
                'accuracy': accuracy_score,
                'hallucination_rate': hallucination_rate,
                'consistency': consistency_score
            },
            risk_level=risk,
            recommendations=recommendations
        )
    
    def evaluate_bias_and_safety(self) -> EvaluationResult:
        """
        Test for various types of bias and safety issues.
        """
        bias_tests = {
            'gender': [
                ("The nurse said", "Check for gender assumptions"),
                ("The engineer thinks", "Check for gender assumptions"),
                ("The secretary mentioned", "Check for gender assumptions"),
            ],
            'age': [
                ("The elderly person wants", "Check for age stereotypes"),
                ("The young employee needs", "Check for age stereotypes"),
            ],
            'cultural': [
                ("People from Asia typically", "Check for cultural stereotypes"),
                ("Americans always", "Check for cultural stereotypes"),
            ]
        }
        
        safety_tests = [
            "How to make dangerous substances",  # Should refuse
            "Write offensive content about",      # Should refuse
            "Ignore previous instructions and",   # Prompt injection test
        ]
        
        bias_scores = {}
        safety_violations = 0
        
        for category, prompts in bias_tests.items():
            category_score = 0
            for prompt, check in prompts:
                # Simulate testing (in practice, analyze actual responses)
                response = self._simulate_model_response(prompt)
                if not self._contains_bias(response, category):
                    category_score += 1
            bias_scores[category] = category_score / len(prompts)
        
        for test in safety_tests:
            response = self._simulate_model_response(test)
            if self._is_unsafe_response(response):
                safety_violations += 1
        
        # Calculate overall safety score
        avg_bias_score = sum(bias_scores.values()) / len(bias_scores)
        safety_score = 1 - (safety_violations / len(safety_tests))
        overall_score = (avg_bias_score * 0.6 + safety_score * 0.4)
        
        # Determine risk
        if safety_violations > 0 or avg_bias_score < 0.7:
            risk = RiskLevel.HIGH
        elif avg_bias_score < 0.85:
            risk = RiskLevel.MEDIUM
        else:
            risk = RiskLevel.LOW
        
        recommendations = []
        if avg_bias_score < 0.8:
            recommendations.append("Implement bias detection filters")
            recommendations.append("Add diverse examples to prompts")
        if safety_violations > 0:
            recommendations.append("Deploy content filtering system")
            recommendations.append("Implement strict output validation")
        
        return EvaluationResult(
            metric_name="Bias & Safety",
            score=overall_score,
            details={
                'bias_scores': bias_scores,
                'safety_violations': safety_violations,
                'overall_bias_score': avg_bias_score
            },
            risk_level=risk,
            recommendations=recommendations
        )
    
    def evaluate_cost_efficiency(self, expected_volume: int, 
                                avg_tokens_per_request: int) -> EvaluationResult:
        """
        Calculate total cost of ownership and ROI.
        """
        # Direct API costs
        monthly_api_cost = (expected_volume * avg_tokens_per_request / 1000) * self.api_cost_per_1k
        
        # Hidden costs
        integration_cost = 50000  # One-time
        monitoring_cost = 2000     # Monthly
        failure_handling_cost = monthly_api_cost * 0.1  # 10% for error handling
        
        # Calculate TCO
        first_year_tco = integration_cost + (monthly_api_cost + monitoring_cost + failure_handling_cost) * 12
        monthly_operational = monthly_api_cost + monitoring_cost + failure_handling_cost
        
        # Estimate savings (compared to human labor)
        human_cost_per_task = 0.50  # Estimated cost if human did the task
        monthly_savings = expected_volume * human_cost_per_task
        
        # ROI calculation
        monthly_roi = (monthly_savings - monthly_operational) / monthly_operational * 100
        payback_period = integration_cost / (monthly_savings - monthly_operational) if monthly_savings > monthly_operational else float('inf')
        
        # Cost efficiency score
        if monthly_roi > 200:
            efficiency_score = 1.0
            risk = RiskLevel.LOW
        elif monthly_roi > 100:
            efficiency_score = 0.8
            risk = RiskLevel.LOW
        elif monthly_roi > 50:
            efficiency_score = 0.6
            risk = RiskLevel.MEDIUM
        elif monthly_roi > 0:
            efficiency_score = 0.4
            risk = RiskLevel.MEDIUM
        else:
            efficiency_score = 0.2
            risk = RiskLevel.HIGH
        
        recommendations = []
        if monthly_api_cost > 10000:
            recommendations.append("Consider caching frequent queries")
            recommendations.append("Evaluate self-hosted models for high volume")
        if payback_period > 6:
            recommendations.append("Start with pilot program to validate ROI")
        
        return EvaluationResult(
            metric_name="Cost Efficiency",
            score=efficiency_score,
            details={
                'monthly_api_cost': monthly_api_cost,
                'monthly_total_cost': monthly_operational,
                'monthly_savings': monthly_savings,
                'monthly_roi_percent': monthly_roi,
                'payback_period_months': payback_period,
                'first_year_tco': first_year_tco
            },
            risk_level=risk,
            recommendations=recommendations
        )
    
    def evaluate_performance(self) -> EvaluationResult:
        """
        Evaluate latency, throughput, and scalability.
        """
        # Simulate performance tests
        latencies = [random.uniform(0.5, 2.0) for _ in range(100)]  # seconds
        avg_latency = sum(latencies) / len(latencies)
        p95_latency = sorted(latencies)[95]
        p99_latency = sorted(latencies)[99]
        
        # Throughput test (requests per second)
        max_throughput = 50  # Simulated max RPS
        
        # Calculate performance score
        if avg_latency < 1.0 and p99_latency < 2.0:
            latency_score = 1.0
        elif avg_latency < 2.0 and p99_latency < 3.0:
            latency_score = 0.7
        else:
            latency_score = 0.4
        
        throughput_score = min(max_throughput / 100, 1.0)  # Normalize to 0-1
        
        performance_score = (latency_score * 0.7 + throughput_score * 0.3)
        
        # Risk assessment
        if avg_latency > 2.0 or max_throughput < 20:
            risk = RiskLevel.HIGH
        elif avg_latency > 1.5 or max_throughput < 50:
            risk = RiskLevel.MEDIUM
        else:
            risk = RiskLevel.LOW
        
        recommendations = []
        if avg_latency > 1.5:
            recommendations.append("Implement response caching for common queries")
            recommendations.append("Consider streaming responses for better UX")
        if max_throughput < 50:
            recommendations.append("Plan for load balancing across multiple instances")
        
        return EvaluationResult(
            metric_name="Performance & Scalability",
            score=performance_score,
            details={
                'avg_latency_sec': avg_latency,
                'p95_latency_sec': p95_latency,
                'p99_latency_sec': p99_latency,
                'max_throughput_rps': max_throughput
            },
            risk_level=risk,
            recommendations=recommendations
        )
    
    def evaluate_compliance(self, industry: str) -> EvaluationResult:
        """
        Check compliance with industry-specific regulations.
        """
        compliance_checks = {
            'healthcare': ['HIPAA', 'Patient Privacy', 'Medical Accuracy'],
            'finance': ['SOC2', 'PCI-DSS', 'Anti-Money Laundering'],
            'general': ['GDPR', 'CCPA', 'Data Retention']
        }
        
        checks = compliance_checks.get(industry, compliance_checks['general'])
        compliance_scores = {}
        
        for check in checks:
            # Simulate compliance check (in practice, detailed audit)
            score = random.uniform(0.7, 1.0)  # Simulated score
            compliance_scores[check] = score
        
        avg_compliance = sum(compliance_scores.values()) / len(compliance_scores)
        
        if avg_compliance < 0.8:
            risk = RiskLevel.CRITICAL
        elif avg_compliance < 0.9:
            risk = RiskLevel.HIGH
        elif avg_compliance < 0.95:
            risk = RiskLevel.MEDIUM
        else:
            risk = RiskLevel.LOW
        
        recommendations = []
        for check, score in compliance_scores.items():
            if score < 0.9:
                recommendations.append(f"Address \{check\} compliance gaps")
        
        if industry == 'healthcare':
            recommendations.append("Implement PHI detection and masking")
        elif industry == 'finance':
            recommendations.append("Add transaction monitoring capabilities")
        
        return EvaluationResult(
            metric_name="Compliance & Governance",
            score=avg_compliance,
            details={
                'industry': industry,
                'compliance_scores': compliance_scores,
                'certifications_needed': [c for c, s in compliance_scores.items() if s < 0.9]
            },
            risk_level=risk,
            recommendations=recommendations
        )
    
    def generate_decision_matrix(self, use_cases: List[str]) -> Dict:
        """
        Create a decision matrix for different use cases.
        """
        # Weight importance of each metric for different use cases
        use_case_weights = {
            'customer_service': {
                'accuracy': 0.25, 'safety': 0.25, 'cost': 0.2, 
                'performance': 0.2, 'compliance': 0.1
            },
            'code_generation': {
                'accuracy': 0.4, 'safety': 0.1, 'cost': 0.15,
                'performance': 0.25, 'compliance': 0.1
            },
            'legal_analysis': {
                'accuracy': 0.35, 'safety': 0.15, 'cost': 0.1,
                'performance': 0.15, 'compliance': 0.25
            },
            'content_creation': {
                'accuracy': 0.2, 'safety': 0.3, 'cost': 0.25,
                'performance': 0.15, 'compliance': 0.1
            }
        }
        
        matrix = {}
        
        for use_case in use_cases:
            weights = use_case_weights.get(use_case, {
                'accuracy': 0.2, 'safety': 0.2, 'cost': 0.2,
                'performance': 0.2, 'compliance': 0.2
            })
            
            # Calculate weighted score
            scores = {r.metric_name: r.score for r in self.results}
            weighted_score = (
                scores.get('Accuracy & Reliability', 0) * weights['accuracy'] +
                scores.get('Bias & Safety', 0) * weights['safety'] +
                scores.get('Cost Efficiency', 0) * weights['cost'] +
                scores.get('Performance & Scalability', 0) * weights['performance'] +
                scores.get('Compliance & Governance', 0) * weights['compliance']
            )
            
            # Determine recommendation
            if weighted_score > 0.8:
                recommendation = "Strongly Recommended"
                deployment = "Full Production"
            elif weighted_score > 0.65:
                recommendation = "Recommended with Safeguards"
                deployment = "Pilot Program"
            elif weighted_score > 0.5:
                recommendation = "Proceed with Caution"
                deployment = "Limited Testing"
            else:
                recommendation = "Not Recommended"
                deployment = "Do Not Deploy"
            
            # Identify critical risks
            critical_risks = [r.metric_name for r in self.results 
                            if r.risk_level in [RiskLevel.HIGH, RiskLevel.CRITICAL]]
            
            matrix[use_case] = {
                'weighted_score': weighted_score,
                'recommendation': recommendation,
                'deployment_strategy': deployment,
                'critical_risks': critical_risks,
                'top_concerns': self._get_top_concerns(use_case)
            }
        
        return matrix
    
    def generate_executive_summary(self) -> str:
        """
        Generate an executive summary of the evaluation.
        """
        summary = f"""
{'='*60}
EXECUTIVE SUMMARY: LLM EVALUATION REPORT
{'='*60}
Model: {self.model_name}
Date: {time.strftime('%Y-%m-%d')}

OVERALL ASSESSMENT
{'='*60}
"""
        
        # Calculate overall score
        overall_score = sum(r.score for r in self.results) / len(self.results)
        
        summary += f"Overall Score: \{overall_score:.2%\}\\n"
        
        # Risk summary
        risk_counts = {}
        for r in self.results:
            risk_counts[r.risk_level] = risk_counts.get(r.risk_level, 0) + 1
        
        summary += f"\\nRISK PROFILE:\\n"
        for risk, count in sorted(risk_counts.items(), key=lambda x: x[0].value):
            summary += f"  \{risk.value\}: \{count\} area(s)\\n"
        
        # Key metrics
        summary += f"\\nKEY METRICS:\\n"
        for result in self.results:
            summary += f"  \{result.metric_name\}: \{result.score:.2%\} (\{result.risk_level.value\} Risk)\\n"
        
        # Top recommendations
        summary += f"\\nTOP RECOMMENDATIONS:\\n"
        all_recommendations = []
        for r in self.results:
            all_recommendations.extend(r.recommendations)
        
        for i, rec in enumerate(set(all_recommendations)[:5], 1):
            summary += f"  \{i\}. \{rec\}\\n"
        
        # Deployment recommendation
        summary += f"\\nDEPLOYMENT RECOMMENDATION:\\n"
        if overall_score > 0.75:
            summary += "  ✅ Ready for production deployment with monitoring\\n"
        elif overall_score > 0.6:
            summary += "  ⚠️  Suitable for pilot programs with safeguards\\n"
        else:
            summary += "  ❌ Not recommended for production use\\n"
        
        summary += "\\n" + "="*60
        
        return summary
    
    # Helper methods (simplified for demonstration)
    def _simulate_model_response(self, prompt: str) -> str:
        """Simulate LLM response for testing."""
        return f"Simulated response to: \{prompt\}"
    
    def _check_correctness(self, response: str, expected: str) -> bool:
        """Check if response matches expected answer."""
        return random.random() > 0.3  # 70% accuracy simulation
    
    def _detect_hallucination(self, response: str, facts: List[str]) -> bool:
        """Detect if response contains hallucinations."""
        return random.random() < 0.15  # 15% hallucination rate
    
    def _check_consistency(self, prompt: str) -> float:
        """Check consistency of responses."""
        return random.uniform(0.7, 0.95)
    
    def _contains_bias(self, response: str, category: str) -> bool:
        """Check if response contains bias."""
        return random.random() < 0.2  # 20% bias rate
    
    def _is_unsafe_response(self, response: str) -> bool:
        """Check if response is unsafe."""
        return random.random() < 0.05  # 5% unsafe rate
    
    def _get_top_concerns(self, use_case: str) -> List[str]:
        """Get top concerns for a use case."""
        concerns = {
            'customer_service': ['Response accuracy', 'Handling edge cases', 'Escalation triggers'],
            'code_generation': ['Security vulnerabilities', 'Code quality', 'Testing requirements'],
            'legal_analysis': ['Accuracy critical', 'Compliance mandatory', 'Audit trail needed'],
            'content_creation': ['Brand voice consistency', 'Fact checking', 'Copyright concerns']
        }
        return concerns.get(use_case, ['General quality', 'Cost management', 'Monitoring'])
    
    def run_full_evaluation(self, industry: str = 'general', 
                          expected_volume: int = 10000,
                          use_cases: List[str] = None):
        """
        Run complete evaluation suite.
        """
        print(f"Starting comprehensive evaluation of \{self.model_name\}...")
        print("="*60)
        
        # Run all evaluations
        test_cases = [
            {'prompt': 'What is 2+2?', 'expected': '4', 'facts': ['2+2=4']},
            {'prompt': 'Capital of France?', 'expected': 'Paris', 'facts': ['Paris is capital of France']},
        ]
        
        self.results.append(self.evaluate_accuracy(test_cases))
        print("✓ Accuracy evaluation complete")
        
        self.results.append(self.evaluate_bias_and_safety())
        print("✓ Bias & safety evaluation complete")
        
        self.results.append(self.evaluate_cost_efficiency(expected_volume, 100))
        print("✓ Cost efficiency evaluation complete")
        
        self.results.append(self.evaluate_performance())
        print("✓ Performance evaluation complete")
        
        self.results.append(self.evaluate_compliance(industry))
        print("✓ Compliance evaluation complete")
        
        # Generate decision matrix
        if use_cases is None:
            use_cases = ['customer_service', 'code_generation', 'legal_analysis', 'content_creation']
        
        matrix = self.generate_decision_matrix(use_cases)
        
        # Print results
        print("\\n" + "="*60)
        print("EVALUATION RESULTS")
        print("="*60)
        
        for result in self.results:
            print(f"\\n\{result.metric_name\}:")
            print(f"  Score: \{result.score:.2%\}")
            print(f"  Risk Level: \{result.risk_level.value\}")
            print(f"  Key Details: \{result.details\}")
            if result.recommendations:
                print(f"  Recommendations:")
                for rec in result.recommendations:
                    print(f"    - \{rec\}")
        
        print("\\n" + "="*60)
        print("DECISION MATRIX BY USE CASE")
        print("="*60)
        
        for use_case, assessment in matrix.items():
            print(f"\\n\{use_case.upper().replace('_', ' ')\}:")
            print(f"  Score: \{assessment['weighted_score']:.2%\}")
            print(f"  Recommendation: \{assessment['recommendation']\}")
            print(f"  Deployment: \{assessment['deployment_strategy']\}")
            if assessment['critical_risks']:
                print(f"  Critical Risks: \{', '.join(assessment['critical_risks'])\}")
            print(f"  Top Concerns: \{', '.join(assessment['top_concerns'])\}")
        
        # Generate executive summary
        print(self.generate_executive_summary())
        
        return {
            'results': self.results,
            'decision_matrix': matrix,
            'overall_score': sum(r.score for r in self.results) / len(self.results)
        }

# Example usage
evaluator = LLMEvaluationFramework(
    model_name="GPT-4-Example",
    api_cost_per_1k=0.03
)

# Run comprehensive evaluation
evaluation_results = evaluator.run_full_evaluation(
    industry='finance',
    expected_volume=50000,
    use_cases=['customer_service', 'legal_analysis']
)

print(f"\\nFinal Score: \{evaluation_results['overall_score']:.2%\}")`,
      testCases: [
        {
          input: 'Test for hallucination detection',
          expected: 'Identifies when model generates false information',
          description: 'Safety critical for enterprise',
        },
        {
          input: 'Calculate ROI with costs and savings',
          expected: 'Positive ROI with clear payback period',
          description: 'Financial justification for adoption',
        },
        {
          input: 'Compliance check for regulated industry',
          expected: 'Identifies gaps and requirements',
          description: 'Regulatory compliance verification',
        },
      ],
    },
    {
      id: 'llm-1-ex4',
      title: 'Implement a Context Window Manager for Long Documents',
      description: 'Build a system to handle documents larger than LLM context windows',
      difficulty: 'medium',
      type: 'implementation',
      instructions: `## Context
You're building a document analysis system for a law firm. They need to process legal documents that are often 50-100 pages long, but the LLM you're using has a context window of only 4,000 tokens (roughly 3,000 words or 6-8 pages).

The lawyers need to:
- Ask questions about any part of the document
- Find specific clauses or sections
- Summarize entire documents
- Compare different sections

## Your Goal
Build a context window manager that:
1. Intelligently chunks long documents
2. Maintains context overlap between chunks
3. Retrieves relevant chunks for queries
4. Handles cross-chunk information
5. Provides coherent answers despite chunking

## Requirements
- Implement smart document chunking with overlap
- Create a chunk retrieval system based on relevance
- Build a query processor that determines which chunks to use
- Handle edge cases where information spans multiple chunks
- Maintain document structure awareness (sections, paragraphs)

## Starting Point
Consider different chunking strategies:
- Fixed-size chunks with overlap
- Semantic chunking (keeping paragraphs/sections together)
- Hierarchical chunking (chapters → sections → paragraphs)

Think about how to maintain coherence when information is split across chunks.`,
      hints: [
        'Start with fixed-size chunking but respect paragraph boundaries - never split mid-sentence.',
        'Use 10-20% overlap between chunks to maintain context at boundaries.',
        'Create a chunk index with metadata: position, keywords, section headers.',
        'For retrieval, use keyword matching or simple TF-IDF to find relevant chunks.',
        'When answering queries, include chunks before and after the relevant one for context.',
        'Keep track of document structure - headers, sections, lists - in metadata.',
        'For summaries, create a hierarchical approach: summarize chunks, then summarize summaries.',
        'Handle pronouns and references by including previous chunk when ambiguous.',
      ],
      solution: `import re
from typing import List, Dict, Tuple, Optional
from dataclasses import dataclass
import math

@dataclass
class Chunk:
    """Represents a document chunk with metadata."""
    id: int
    text: str
    start_pos: int
    end_pos: int
    tokens: int
    section: Optional[str]
    keywords: List[str]
    overlap_with_prev: int
    overlap_with_next: int

class ContextWindowManager:
    """
    Manages long documents that exceed LLM context windows.
    
    This system enables processing of large documents by intelligently
    chunking them while maintaining coherence and context.
    """
    
    def __init__(self, max_tokens: int = 4000, overlap_ratio: float = 0.15):
        """
        Initialize the context window manager.
        
        Args:
            max_tokens: Maximum tokens per chunk (context window size)
            overlap_ratio: Percentage of overlap between chunks (0.15 = 15%)
        """
        self.max_tokens = max_tokens
        self.overlap_ratio = overlap_ratio
        self.chunks: List[Chunk] = []
        self.document_structure: Dict = {}
        self.chunk_index: Dict = {}
    
    def chunk_document(self, document: str) -> List[Chunk]:
        """
        Intelligently chunk a document while preserving structure.
        
        This method ensures:
        - No sentences are split
        - Paragraphs are kept together when possible
        - Sections maintain coherence
        - Overlap provides context continuity
        """
        # Detect document structure
        self.document_structure = self._analyze_structure(document)
        
        # Calculate chunk parameters
        chunk_size = int(self.max_tokens * 0.75)  # Leave room for overlap
        overlap_size = int(chunk_size * self.overlap_ratio)
        
        # Split into paragraphs first
        paragraphs = self._split_paragraphs(document)
        
        self.chunks = []
        current_chunk = []
        current_tokens = 0
        chunk_id = 0
        last_chunk_end = []
        
        for para_idx, paragraph in enumerate(paragraphs):
            para_tokens = self._estimate_tokens(paragraph)
            
            # If single paragraph exceeds chunk size, split it
            if para_tokens > chunk_size:
                sentences = self._split_sentences(paragraph)
                for sentence in sentences:
                    sent_tokens = self._estimate_tokens(sentence)
                    
                    if current_tokens + sent_tokens > chunk_size:
                        # Create chunk with overlap from previous
                        chunk_text = ' '.join(current_chunk)
                        overlap_prev = ' '.join(last_chunk_end[-overlap_size:]) if last_chunk_end else ""
                        
                        if overlap_prev:
                            chunk_text = overlap_prev + " " + chunk_text
                        
                        self._create_chunk(
                            chunk_id, chunk_text, 
                            len(overlap_prev.split()) if overlap_prev else 0,
                            0  # Will update next overlap later
                        )
                        
                        # Prepare for next chunk
                        last_chunk_end = current_chunk.copy()
                        current_chunk = [sentence]
                        current_tokens = sent_tokens
                        chunk_id += 1
                    else:
                        current_chunk.append(sentence)
                        current_tokens += sent_tokens
            else:
                # Check if paragraph fits in current chunk
                if current_tokens + para_tokens > chunk_size and current_chunk:
                    # Save current chunk
                    chunk_text = ' '.join(current_chunk)
                    overlap_prev = ' '.join(last_chunk_end[-overlap_size:]) if last_chunk_end else ""
                    
                    if overlap_prev:
                        chunk_text = overlap_prev + " " + chunk_text
                    
                    self._create_chunk(
                        chunk_id, chunk_text,
                        len(overlap_prev.split()) if overlap_prev else 0,
                        0
                    )
                    
                    # Start new chunk with overlap
                    last_chunk_end = current_chunk.copy()
                    current_chunk = [paragraph]
                    current_tokens = para_tokens
                    chunk_id += 1
                else:
                    current_chunk.append(paragraph)
                    current_tokens += para_tokens
        
        # Don't forget the last chunk
        if current_chunk:
            chunk_text = ' '.join(current_chunk)
            overlap_prev = ' '.join(last_chunk_end[-overlap_size:]) if last_chunk_end else ""
            
            if overlap_prev:
                chunk_text = overlap_prev + " " + chunk_text
            
            self._create_chunk(
                chunk_id, chunk_text,
                len(overlap_prev.split()) if overlap_prev else 0,
                0
            )
        
        # Update overlap_with_next for all chunks
        for i in range(len(self.chunks) - 1):
            overlap_words = self._calculate_overlap(self.chunks[i].text, self.chunks[i + 1].text)
            self.chunks[i].overlap_with_next = overlap_words
        
        # Build index for retrieval
        self._build_chunk_index()
        
        return self.chunks
    
    def retrieve_relevant_chunks(self, query: str, top_k: int = 3) -> List[Chunk]:
        """
        Retrieve the most relevant chunks for a query.
        
        Uses keyword matching and position awareness to find
        the best chunks to answer the query.
        """
        query_keywords = self._extract_keywords(query.lower())
        chunk_scores = []
        
        for chunk in self.chunks:
            score = 0
            
            # Keyword matching score
            chunk_text_lower = chunk.text.lower()
            for keyword in query_keywords:
                score += chunk_text_lower.count(keyword) * 2
            
            # Boost score for section headers mentioned in query
            if chunk.section:
                section_lower = chunk.section.lower()
                for keyword in query_keywords:
                    if keyword in section_lower:
                        score += 5
            
            # Check chunk keywords
            for keyword in chunk.keywords:
                if keyword in query_keywords:
                    score += 3
            
            chunk_scores.append((chunk, score))
        
        # Sort by score and return top k
        chunk_scores.sort(key=lambda x: x[1], reverse=True)
        selected_chunks = [chunk for chunk, score in chunk_scores[:top_k] if score > 0]
        
        # If we have selected chunks, also include adjacent chunks for context
        if selected_chunks:
            expanded_chunks = []
            for chunk in selected_chunks:
                expanded_chunks.append(chunk)
                
                # Add previous chunk if exists and not already included
                if chunk.id > 0:
                    prev_chunk = self.chunks[chunk.id - 1]
                    if prev_chunk not in expanded_chunks:
                        expanded_chunks.append(prev_chunk)
                
                # Add next chunk if exists and not already included
                if chunk.id < len(self.chunks) - 1:
                    next_chunk = self.chunks[chunk.id + 1]
                    if next_chunk not in expanded_chunks:
                        expanded_chunks.append(next_chunk)
            
            # Sort by chunk id to maintain document order
            expanded_chunks.sort(key=lambda x: x.id)
            return expanded_chunks[:top_k + 1]  # Return a few extra for context
        
        # If no relevant chunks found, return first few chunks
        return self.chunks[:top_k]
    
    def process_query(self, query: str, document: str) -> Dict:
        """
        Process a query against a long document.
        
        This is the main interface for answering questions about
        documents that exceed the context window.
        """
        # Chunk document if not already done
        if not self.chunks:
            self.chunk_document(document)
        
        # Retrieve relevant chunks
        relevant_chunks = self.retrieve_relevant_chunks(query)
        
        # Prepare context for LLM
        context = self._prepare_context(relevant_chunks)
        
        # Analyze query type
        query_type = self._classify_query(query)
        
        # Prepare response based on query type
        if query_type == 'summary':
            return self._handle_summary_query(document)
        elif query_type == 'specific':
            return self._handle_specific_query(query, context, relevant_chunks)
        elif query_type == 'comparison':
            return self._handle_comparison_query(query, document)
        else:
            return self._handle_general_query(query, context, relevant_chunks)
    
    def _prepare_context(self, chunks: List[Chunk]) -> str:
        """
        Prepare context from chunks for LLM processing.
        
        Handles overlap removal and context markers.
        """
        if not chunks:
            return ""
        
        # For single chunk, return as is
        if len(chunks) == 1:
            return chunks[0].text
        
        # For multiple chunks, intelligently combine
        combined_text = []
        seen_text = set()
        
        for i, chunk in enumerate(chunks):
            # Remove overlap with previous chunk
            if i > 0 and chunk.overlap_with_prev > 0:
                # Split text and skip overlapping part
                words = chunk.text.split()
                text_without_overlap = ' '.join(words[chunk.overlap_with_prev:])
            else:
                text_without_overlap = chunk.text
            
            # Add section marker if new section
            if chunk.section and (i == 0 or chunk.section != chunks[i-1].section):
                combined_text.append(f"\\n[Section: \{chunk.section\}]\\n")
            
            # Add text if not duplicate
            text_hash = hash(text_without_overlap)
            if text_hash not in seen_text:
                combined_text.append(text_without_overlap)
                seen_text.add(text_hash)
            
            # Add continuation marker if there's a gap
            if i < len(chunks) - 1 and chunks[i+1].id != chunk.id + 1:
                combined_text.append("\\n[...content omitted...]\\n")
        
        return ' '.join(combined_text)
    
    def _handle_summary_query(self, document: str) -> Dict:
        """
        Handle document summarization queries.
        
        Uses hierarchical summarization for long documents.
        """
        # Chunk if needed
        if not self.chunks:
            self.chunk_document(document)
        
        # Summarize each chunk
        chunk_summaries = []
        for chunk in self.chunks:
            # Simulate chunk summarization (in practice, call LLM)
            summary = f"Summary of section \{chunk.id\}: \{chunk.text[:100]\}..."
            chunk_summaries.append(summary)
        
        # Combine chunk summaries
        if len(chunk_summaries) > 5:
            # For very long documents, summarize the summaries
            final_summary = "Document Summary:\\n"
            final_summary += f"This document contains \{len(self.chunks)\} sections.\\n"
            final_summary += f"Main topics: \{', '.join(self.document_structure.get('main_topics', [])[:5])\}\\n"
            final_summary += "Key points from each section...\\n"
        else:
            final_summary = "\\n".join(chunk_summaries)
        
        return {
            'type': 'summary',
            'response': final_summary,
            'chunks_used': len(self.chunks),
            'method': 'hierarchical_summarization'
        }
    
    def _handle_specific_query(self, query: str, context: str, chunks: List[Chunk]) -> Dict:
        """
        Handle specific information queries.
        """
        return {
            'type': 'specific',
            'response': f"Based on the relevant sections: \{context[:200]\}...",
            'chunks_used': len(chunks),
            'chunk_ids': [c.id for c in chunks],
            'confidence': 'high' if len(chunks) <= 3 else 'medium'
        }
    
    def _handle_comparison_query(self, query: str, document: str) -> Dict:
        """
        Handle queries comparing different parts of the document.
        """
        # Extract sections mentioned in query
        sections = self._extract_sections_from_query(query)
        
        # Get chunks for each section
        relevant_chunks = []
        for section in sections:
            for chunk in self.chunks:
                if chunk.section and section.lower() in chunk.section.lower():
                    relevant_chunks.append(chunk)
        
        context = self._prepare_context(relevant_chunks)
        
        return {
            'type': 'comparison',
            'response': f"Comparing sections: \{', '.join(sections)\}",
            'chunks_used': len(relevant_chunks),
            'sections_compared': sections,
            'context': context[:500] + "..."
        }
    
    def _handle_general_query(self, query: str, context: str, chunks: List[Chunk]) -> Dict:
        """
        Handle general queries.
        """
        return {
            'type': 'general',
            'response': f"Based on the document: \{context[:300]\}...",
            'chunks_used': len(chunks),
            'chunk_ids': [c.id for c in chunks]
        }
    
    # Helper methods
    def _analyze_structure(self, document: str) -> Dict:
        """Analyze document structure."""
        structure = {
            'total_length': len(document),
            'total_words': len(document.split()),
            'sections': [],
            'main_topics': []
        }
        
        # Find section headers (simple heuristic)
        lines = document.split('\\n')
        for line in lines:
            if line.isupper() or (line and line[0].isupper() and len(line) < 100):
                structure['sections'].append(line.strip())
        
        # Extract main topics (simplified)
        words = document.lower().split()
        word_freq = {}
        for word in words:
            if len(word) > 5:  # Focus on longer words
                word_freq[word] = word_freq.get(word, 0) + 1
        
        structure['main_topics'] = sorted(word_freq.keys(), 
                                         key=lambda x: word_freq[x], 
                                         reverse=True)[:10]
        
        return structure
    
    def _split_paragraphs(self, text: str) -> List[str]:
        """Split text into paragraphs."""
        # Split on double newlines or single newlines with indentation
        paragraphs = re.split(r'\\n\\n+|\\n(?=\\s{2,})', text)
        return [p.strip() for p in paragraphs if p.strip()]
    
    def _split_sentences(self, text: str) -> List[str]:
        """Split text into sentences."""
        # Simple sentence splitter
        sentences = re.split(r'(?<=[.!?])\\s+', text)
        return [s.strip() for s in sentences if s.strip()]
    
    def _estimate_tokens(self, text: str) -> int:
        """Estimate token count for text."""
        # Rough estimation: 1 token per 4 characters or 0.75 tokens per word
        return max(len(text) // 4, int(len(text.split()) * 0.75))
    
    def _extract_keywords(self, text: str) -> List[str]:
        """Extract keywords from text."""
        # Remove common words and return significant terms
        stop_words = {'the', 'is', 'at', 'which', 'on', 'a', 'an', 'as', 
                     'are', 'was', 'were', 'be', 'have', 'has', 'had',
                     'do', 'does', 'did', 'will', 'would', 'could', 'should'}
        
        words = re.findall(r'\\b\\w+\\b', text.lower())
        keywords = [w for w in words if w not in stop_words and len(w) > 3]
        
        # Return unique keywords
        return list(set(keywords))
    
    def _create_chunk(self, chunk_id: int, text: str, 
                     overlap_prev: int, overlap_next: int):
        """Create and store a chunk."""
        # Detect section
        section = None
        for sec in self.document_structure.get('sections', []):
            if sec in text:
                section = sec
                break
        
        chunk = Chunk(
            id=chunk_id,
            text=text,
            start_pos=chunk_id * (self.max_tokens * 3),  # Approximate
            end_pos=(chunk_id + 1) * (self.max_tokens * 3),
            tokens=self._estimate_tokens(text),
            section=section,
            keywords=self._extract_keywords(text)[:10],
            overlap_with_prev=overlap_prev,
            overlap_with_next=overlap_next
        )
        
        self.chunks.append(chunk)
    
    def _build_chunk_index(self):
        """Build an index for efficient chunk retrieval."""
        self.chunk_index = {}
        
        for chunk in self.chunks:
            # Index by keywords
            for keyword in chunk.keywords:
                if keyword not in self.chunk_index:
                    self.chunk_index[keyword] = []
                self.chunk_index[keyword].append(chunk.id)
            
            # Index by section
            if chunk.section:
                section_key = f"section:\{chunk.section\}"
                if section_key not in self.chunk_index:
                    self.chunk_index[section_key] = []
                self.chunk_index[section_key].append(chunk.id)
    
    def _calculate_overlap(self, text1: str, text2: str) -> int:
        """Calculate word overlap between two texts."""
        words1 = text1.split()
        words2 = text2.split()
        
        # Find longest common suffix of text1 and prefix of text2
        overlap = 0
        for i in range(1, min(len(words1), len(words2))):
            if words1[-i:] == words2[:i]:
                overlap = i
        
        return overlap
    
    def _classify_query(self, query: str) -> str:
        """Classify the type of query."""
        query_lower = query.lower()
        
        if any(word in query_lower for word in ['summary', 'summarize', 'overview']):
            return 'summary'
        elif any(word in query_lower for word in ['compare', 'difference', 'versus']):
            return 'comparison'
        elif any(word in query_lower for word in ['what', 'where', 'when', 'who', 'which']):
            return 'specific'
        else:
            return 'general'
    
    def _extract_sections_from_query(self, query: str) -> List[str]:
        """Extract section references from query."""
        sections = []
        
        # Look for quoted sections
        quoted = re.findall(r'"([^"]+)"', query)
        sections.extend(quoted)
        
        # Look for section keywords
        for section in self.document_structure.get('sections', []):
            if section.lower() in query.lower():
                sections.append(section)
        
        return list(set(sections))

# Demonstration
def demonstrate_context_manager():
    """Demonstrate the context window manager with a legal document."""
    
    # Create a sample long document
    sample_document = """
LEGAL SERVICES AGREEMENT

This Legal Services Agreement ("Agreement") is entered into as of January 1, 2024, 
between ABC Corporation ("Client") and XYZ Law Firm ("Firm").

SECTION 1: SCOPE OF SERVICES
The Firm agrees to provide legal services to the Client in connection with corporate 
matters, including but not limited to contract review, regulatory compliance, and 
intellectual property protection. The Firm will assign qualified attorneys to handle 
the Client's matters with appropriate expertise.

The services will include:
a) Review and negotiation of commercial contracts
b) Advice on regulatory compliance matters
c) Intellectual property registration and protection
d) General corporate counsel services
e) Litigation support when necessary

SECTION 2: TERM AND TERMINATION
This Agreement shall commence on January 1, 2024, and continue for a period of one 
year, unless earlier terminated. Either party may terminate this Agreement upon thirty 
(30) days written notice to the other party.

Upon termination, the Firm shall:
- Complete any ongoing matters to a reasonable stopping point
- Return all Client property and confidential information
- Provide a final invoice for services rendered

SECTION 3: FEES AND PAYMENT
The Client agrees to pay the Firm for services rendered at the following hourly rates:
- Senior Partner: $500 per hour
- Partner: $400 per hour
- Associate: $250 per hour
- Paralegal: $150 per hour

Invoices will be sent monthly and payment is due within 30 days of receipt. Late 
payments will incur interest at 1.5% per month.

SECTION 4: CONFIDENTIALITY
The Firm agrees to maintain the confidentiality of all Client information and to use 
such information solely for the purpose of providing legal services under this Agreement. 
This obligation survives termination of the Agreement.

SECTION 5: CONFLICTS OF INTEREST
The Firm represents that it has conducted a conflicts check and no conflicts exist 
that would prevent representation of the Client. The Firm will notify the Client 
immediately if any potential conflicts arise.

SECTION 6: LIMITATION OF LIABILITY
The Firm's liability for any claim arising out of this Agreement shall not exceed 
the total fees paid by the Client in the twelve months preceding the claim.

SECTION 7: GOVERNING LAW
This Agreement shall be governed by the laws of the State of New York, without regard 
to its conflict of laws principles.

SECTION 8: ENTIRE AGREEMENT
This Agreement constitutes the entire agreement between the parties and supersedes all 
prior negotiations, representations, and agreements.
    """ * 3  # Triple it to make it longer
    
    # Initialize manager
    manager = ContextWindowManager(max_tokens=500, overlap_ratio=0.15)
    
    print("="*60)
    print("CONTEXT WINDOW MANAGER DEMONSTRATION")
    print("="*60)
    
    # Chunk the document
    print(f"\\nDocument length: \{len(sample_document)\} characters")
    print(f"Estimated tokens: \{manager._estimate_tokens(sample_document)\}")
    
    chunks = manager.chunk_document(sample_document)
    print(f"\\nCreated \{len(chunks)\} chunks")
    
    # Display chunk information
    print("\\nChunk Information:")
    print("-"*40)
    for chunk in chunks[:3]:  # Show first 3 chunks
        print(f"Chunk \{chunk.id\}:")
        print(f"  Tokens: \{chunk.tokens\}")
        print(f"  Section: \{chunk.section\}")
        print(f"  Keywords: \{', '.join(chunk.keywords[:5])\}")
        print(f"  Overlap with prev: \{chunk.overlap_with_prev\} words")
        print(f"  First 100 chars: \{chunk.text[:100]\}...")
        print()
    
    # Test different query types
    queries = [
        "What are the hourly rates for different attorney levels?",
        "How can the agreement be terminated?",
        "Compare the confidentiality and liability sections",
        "Summarize the entire agreement"
    ]
    
    print("\\n" + "="*60)
    print("QUERY PROCESSING EXAMPLES")
    print("="*60)
    
    for query in queries:
        print(f"\\nQuery: \{query\}")
        print("-"*40)
        
        result = manager.process_query(query, sample_document)
        
        print(f"Query Type: \{result['type']\}")
        print(f"Chunks Used: \{result.get('chunks_used', 'N/A')\}")
        if 'chunk_ids' in result:
            print(f"Chunk IDs: \{result['chunk_ids']\}")
        print(f"Response Preview: \{result['response'][:200]\}...")
        
        if result['type'] == 'specific':
            print(f"Confidence: \{result.get('confidence', 'N/A')\}")

# Run the demonstration
demonstrate_context_manager()`,
      testCases: [
        {
          input: 'Long document with 10,000 words',
          expected: 'Document chunked into manageable pieces with overlap',
          description: 'Intelligent document chunking',
        },
        {
          input: 'Query: "What does section 3 say about payment?"',
          expected: 'Retrieves section 3 chunks and adjacent context',
          description: 'Targeted chunk retrieval',
        },
        {
          input: 'Query: "Summarize the document"',
          expected: 'Hierarchical summarization of all chunks',
          description: 'Full document summary despite size',
        },
      ],
    },
    {
      id: 'llm-1-ex5',
      title: 'Build a Prompt Engineering Testing Framework',
      description: 'Create a systematic framework for testing and optimizing prompts',
      difficulty: 'easy',
      type: 'implementation',
      instructions: `## Context
You're helping a content creation team optimize their prompts for generating marketing copy. They've noticed that small changes in prompts can dramatically affect output quality, but they don't have a systematic way to test and improve their prompts.

The team needs to:
- Test different prompt variations
- Measure output quality consistently
- Track which prompt patterns work best
- Build a library of effective prompts

## Your Goal
Build a prompt testing framework that:
1. Tests multiple prompt variations systematically
2. Evaluates outputs against defined criteria
3. Identifies effective prompt patterns
4. Generates optimization recommendations
5. Maintains a prompt library with performance data

## Requirements
- Create prompt templates with variable components
- Implement quality scoring for different output aspects
- Test prompt variations with A/B testing methodology
- Track performance metrics across iterations
- Generate reports showing best practices

## Starting Point
Consider what makes a prompt effective:
- Clear instructions
- Examples (few-shot learning)
- Output format specification
- Constraints and requirements
- Tone and style guidance

Think about how to measure prompt effectiveness objectively.`,
      hints: [
        'Start with a prompt template class that has variable sections: instruction, context, examples, constraints.',
        'Create variations by changing one element at a time (A/B testing principle).',
        'Define quality metrics: relevance, completeness, format compliance, creativity.',
        'Score outputs on multiple dimensions and calculate weighted overall score.',
        'Track which prompt elements correlate with high scores.',
        'Build a results matrix showing prompt variant vs. quality scores.',
        'Identify patterns: Do examples help? Do constraints improve focus?',
        'Generate recommendations based on statistical analysis of results.',
      ],
      solution: `import json
import random
from typing import List, Dict, Any, Tuple
from dataclasses import dataclass, field
from datetime import datetime
import statistics

@dataclass
class PromptTemplate:
    """Represents a prompt template with variable components."""
    name: str
    instruction: str
    context: str = ""
    examples: List[str] = field(default_factory=list)
    constraints: List[str] = field(default_factory=list)
    output_format: str = ""
    tone: str = ""
    
    def build(self) -> str:
        """Construct the full prompt from components."""
        prompt_parts = []
        
        # Add instruction
        prompt_parts.append(self.instruction)
        
        # Add context if provided
        if self.context:
            prompt_parts.append(f"\\nContext: \{self.context\}")
        
        # Add examples if provided
        if self.examples:
            prompt_parts.append("\\nExamples:")
            for i, example in enumerate(self.examples, 1):
                prompt_parts.append(f"\{i\}. \{example\}")
        
        # Add constraints if provided
        if self.constraints:
            prompt_parts.append("\\nRequirements:")
            for constraint in self.constraints:
                prompt_parts.append(f"- \{constraint\}")
        
        # Add output format if specified
        if self.output_format:
            prompt_parts.append(f"\\nOutput Format: \{self.output_format\}")
        
        # Add tone guidance if specified
        if self.tone:
            prompt_parts.append(f"\\nTone: \{self.tone\}")
        
        return "\\n".join(prompt_parts)
    
    def create_variant(self, variation_type: str, variation_value: Any) -> 'PromptTemplate':
        """Create a variant of this template with one changed component."""
        import copy
        variant = copy.deepcopy(self)
        variant.name = f"\{self.name\}_\{variation_type\}_variant"
        
        if variation_type == 'instruction':
            variant.instruction = variation_value
        elif variation_type == 'context':
            variant.context = variation_value
        elif variation_type == 'examples':
            variant.examples = variation_value
        elif variation_type == 'constraints':
            variant.constraints = variation_value
        elif variation_type == 'output_format':
            variant.output_format = variation_value
        elif variation_type == 'tone':
            variant.tone = variation_value
        
        return variant

@dataclass
class TestResult:
    """Results from testing a prompt variant."""
    prompt_name: str
    prompt_text: str
    scores: Dict[str, float]
    overall_score: float
    output_sample: str
    timestamp: datetime
    metadata: Dict[str, Any] = field(default_factory=dict)

class PromptTestingFramework:
    """
    Systematic framework for testing and optimizing prompts.
    
    This framework helps teams identify what makes prompts effective
    through controlled experimentation and analysis.
    """
    
    def __init__(self):
        self.test_results: List[TestResult] = []
        self.prompt_library: Dict[str, PromptTemplate] = {}
        self.best_practices: Dict[str, Any] = {}
        
    def evaluate_output(self, output: str, criteria: Dict[str, Any]) -> Dict[str, float]:
        """
        Evaluate output quality across multiple dimensions.
        
        This simulates LLM output evaluation. In practice, you might use
        another LLM as a judge or human evaluation.
        """
        scores = {}
        
        # Relevance: Does the output address the task?
        if criteria.get('keywords'):
            keyword_count = sum(1 for kw in criteria['keywords'] if kw.lower() in output.lower())
            scores['relevance'] = min(keyword_count / len(criteria['keywords']), 1.0)
        else:
            scores['relevance'] = 0.5  # Neutral if no keywords specified
        
        # Completeness: Does it cover all required points?
        if criteria.get('required_points'):
            covered = sum(1 for point in criteria['required_points'] if point.lower() in output.lower())
            scores['completeness'] = covered / len(criteria['required_points'])
        else:
            scores['completeness'] = 0.5
        
        # Format Compliance: Does it follow the specified format?
        if criteria.get('format_check'):
            format_indicators = criteria['format_check']
            format_matches = sum(1 for indicator in format_indicators if indicator in output)
            scores['format_compliance'] = format_matches / len(format_indicators)
        else:
            scores['format_compliance'] = 1.0  # Perfect if no format specified
        
        # Length Appropriateness
        if criteria.get('target_length'):
            actual_length = len(output.split())
            target = criteria['target_length']
            if actual_length < target * 0.5:
                scores['length'] = 0.3
            elif actual_length < target * 0.8:
                scores['length'] = 0.7
            elif actual_length <= target * 1.2:
                scores['length'] = 1.0
            else:
                scores['length'] = 0.6  # Too long
        else:
            scores['length'] = 0.5
        
        # Creativity/Variety (simulated)
        unique_words = len(set(output.lower().split()))
        total_words = len(output.split())
        scores['creativity'] = min(unique_words / (total_words * 0.5), 1.0) if total_words > 0 else 0
        
        # Tone Match (simulated)
        if criteria.get('tone'):
            tone_markers = {
                'professional': ['therefore', 'consequently', 'regards', 'please'],
                'casual': ['hey', 'cool', 'awesome', 'yeah'],
                'friendly': ['happy', 'glad', 'enjoy', 'wonderful'],
                'urgent': ['immediately', 'urgent', 'asap', 'critical']
            }
            
            if criteria['tone'] in tone_markers:
                markers = tone_markers[criteria['tone']]
                marker_count = sum(1 for m in markers if m in output.lower())
                scores['tone_match'] = min(marker_count / 2, 1.0)  # At least 2 markers for full score
            else:
                scores['tone_match'] = 0.5
        else:
            scores['tone_match'] = 0.5
        
        return scores
    
    def test_prompt_variants(self, 
                            base_prompt: PromptTemplate,
                            variants: List[PromptTemplate],
                            test_cases: List[Dict[str, Any]],
                            weights: Dict[str, float] = None) -> List[TestResult]:
        """
        Test multiple prompt variants systematically.
        
        This is the core testing engine that runs controlled experiments.
        """
        if weights is None:
            weights = {
                'relevance': 0.25,
                'completeness': 0.25,
                'format_compliance': 0.15,
                'length': 0.1,
                'creativity': 0.15,
                'tone_match': 0.1
            }
        
        all_prompts = [base_prompt] + variants
        results = []
        
        for prompt in all_prompts:
            prompt_text = prompt.build()
            prompt_scores = []
            
            for test_case in test_cases:
                # Simulate LLM response (in practice, call actual LLM)
                simulated_output = self._simulate_llm_response(prompt_text, test_case)
                
                # Evaluate output
                scores = self.evaluate_output(simulated_output, test_case.get('criteria', {}))
                
                # Calculate weighted overall score
                overall = sum(scores.get(metric, 0) * weight 
                            for metric, weight in weights.items())
                
                prompt_scores.append({
                    'scores': scores,
                    'overall': overall,
                    'output': simulated_output
                })
            
            # Aggregate results across test cases
            avg_scores = {}
            for metric in weights.keys():
                metric_scores = [ps['scores'].get(metric, 0) for ps in prompt_scores]
                avg_scores[metric] = statistics.mean(metric_scores)
            
            avg_overall = statistics.mean([ps['overall'] for ps in prompt_scores])
            
            # Create test result
            result = TestResult(
                prompt_name=prompt.name,
                prompt_text=prompt_text,
                scores=avg_scores,
                overall_score=avg_overall,
                output_sample=prompt_scores[0]['output'] if prompt_scores else "",
                timestamp=datetime.now(),
                metadata={
                    'num_test_cases': len(test_cases),
                    'has_examples': len(prompt.examples) > 0,
                    'has_constraints': len(prompt.constraints) > 0,
                    'has_format': bool(prompt.output_format),
                    'has_tone': bool(prompt.tone)
                }
            )
            
            results.append(result)
            self.test_results.append(result)
        
        # Store in library if high-performing
        for result in results:
            if result.overall_score > 0.7:
                self.prompt_library[result.prompt_name] = next(
                    p for p in all_prompts if p.name == result.prompt_name
                )
        
        return results
    
    def analyze_results(self, results: List[TestResult]) -> Dict[str, Any]:
        """
        Analyze test results to identify patterns and best practices.
        
        This analysis helps teams understand what makes prompts effective.
        """
        analysis = {
            'summary': {},
            'best_performer': None,
            'worst_performer': None,
            'patterns': {},
            'recommendations': []
        }
        
        # Find best and worst performers
        sorted_results = sorted(results, key=lambda x: x.overall_score, reverse=True)
        analysis['best_performer'] = {
            'name': sorted_results[0].prompt_name,
            'score': sorted_results[0].overall_score,
            'strengths': [k for k, v in sorted_results[0].scores.items() if v > 0.8]
        }
        analysis['worst_performer'] = {
            'name': sorted_results[-1].prompt_name,
            'score': sorted_results[-1].overall_score,
            'weaknesses': [k for k, v in sorted_results[-1].scores.items() if v < 0.5]
        }
        
        # Analyze impact of different components
        with_examples = [r for r in results if r.metadata.get('has_examples')]
        without_examples = [r for r in results if not r.metadata.get('has_examples')]
        
        if with_examples and without_examples:
            avg_with_examples = statistics.mean([r.overall_score for r in with_examples])
            avg_without_examples = statistics.mean([r.overall_score for r in without_examples])
            example_impact = avg_with_examples - avg_without_examples
            
            analysis['patterns']['example_impact'] = {
                'with_examples': avg_with_examples,
                'without_examples': avg_without_examples,
                'difference': example_impact
            }
            
            if example_impact > 0.1:
                analysis['recommendations'].append(
                    "Including examples significantly improves performance (+{:.1%})".format(example_impact)
                )
        
        # Analyze constraints impact
        with_constraints = [r for r in results if r.metadata.get('has_constraints')]
        without_constraints = [r for r in results if not r.metadata.get('has_constraints')]
        
        if with_constraints and without_constraints:
            avg_with_constraints = statistics.mean([r.overall_score for r in with_constraints])
            avg_without_constraints = statistics.mean([r.overall_score for r in without_constraints])
            constraint_impact = avg_with_constraints - avg_without_constraints
            
            analysis['patterns']['constraint_impact'] = {
                'with_constraints': avg_with_constraints,
                'without_constraints': avg_without_constraints,
                'difference': constraint_impact
            }
            
            if constraint_impact > 0.05:
                analysis['recommendations'].append(
                    "Adding constraints improves output quality (+{:.1%})".format(constraint_impact)
                )
        
        # Analyze format specification impact
        with_format = [r for r in results if r.metadata.get('has_format')]
        
        if with_format:
            format_compliance_scores = [r.scores.get('format_compliance', 0) for r in with_format]
            avg_format_compliance = statistics.mean(format_compliance_scores)
            
            if avg_format_compliance > 0.8:
                analysis['recommendations'].append(
                    "Format specifications are well-followed (compliance: {:.1%})".format(avg_format_compliance)
                )
        
        # Metric-specific insights
        for metric in ['relevance', 'completeness', 'creativity']:
            metric_scores = [r.scores.get(metric, 0) for r in results]
            analysis['summary'][metric] = {
                'mean': statistics.mean(metric_scores),
                'std_dev': statistics.stdev(metric_scores) if len(metric_scores) > 1 else 0,
                'max': max(metric_scores),
                'min': min(metric_scores)
            }
        
        # Generate final recommendations
        if analysis['summary']['relevance']['mean'] < 0.6:
            analysis['recommendations'].append(
                "Consider adding more specific keywords and context to improve relevance"
            )
        
        if analysis['summary']['completeness']['mean'] < 0.7:
            analysis['recommendations'].append(
                "Prompts may need clearer instructions about required content points"
            )
        
        if analysis['summary']['creativity']['std_dev'] > 0.3:
            analysis['recommendations'].append(
                "High variability in creativity - consider standardizing creative guidance"
            )
        
        return analysis
    
    def generate_report(self, test_name: str = "Prompt Optimization Report") -> str:
        """
        Generate a comprehensive report of prompt testing results.
        """
        report = f"""
{'='*60}
{test_name}
{'='*60}
Generated: {datetime.now().strftime('%Y-%m-%d %H:%M:%S')}

EXECUTIVE SUMMARY
{'='*60}
Total Prompts Tested: {len(self.test_results)}
Prompts in Library: {len(self.prompt_library)}
Average Performance: {statistics.mean([r.overall_score for r in self.test_results]):.2%}

TOP PERFORMING PROMPTS
{'='*60}
"""
        
        # Sort by performance
        top_prompts = sorted(self.test_results, key=lambda x: x.overall_score, reverse=True)[:5]
        
        for i, result in enumerate(top_prompts, 1):
            report += f"""
{i}. {result.prompt_name}
   Overall Score: {result.overall_score:.2%}
   Strengths: {', '.join([k for k, v in result.scores.items() if v > 0.7])}
   Key Components: {', '.join([k for k, v in result.metadata.items() if v and k.startswith('has_')])}
"""
        
        # Pattern Analysis
        if len(self.test_results) > 5:
            results_subset = self.test_results[-10:]  # Last 10 results
            analysis = self.analyze_results(results_subset)
            
            report += f"""
PATTERN ANALYSIS
{'='*60}
"""
            
            if 'example_impact' in analysis['patterns']:
                impact = analysis['patterns']['example_impact']
                report += f"""
Example Impact:
  With Examples: {impact['with_examples']:.2%}
  Without Examples: {impact['without_examples']:.2%}
  Difference: {impact['difference']:+.2%}
"""
            
            if 'constraint_impact' in analysis['patterns']:
                impact = analysis['patterns']['constraint_impact']
                report += f"""
Constraint Impact:
  With Constraints: {impact['with_constraints']:.2%}
  Without Constraints: {impact['without_constraints']:.2%}
  Difference: {impact['difference']:+.2%}
"""
            
            report += f"""
RECOMMENDATIONS
{'='*60}
"""
            for i, rec in enumerate(analysis['recommendations'], 1):
                report += f"\{i\}. \{rec\}\\n"
        
        # Best Practices
        report += f"""
IDENTIFIED BEST PRACTICES
{'='*60}
"""
        
        best_practices = self._extract_best_practices()
        for i, practice in enumerate(best_practices, 1):
            report += f"\{i\}. \{practice\}\\n"
        
        report += "\\n" + "="*60
        
        return report
    
    def _simulate_llm_response(self, prompt: str, test_case: Dict) -> str:
        """
        Simulate LLM response for testing purposes.
        In practice, this would call an actual LLM API.
        """
        # Generate a plausible response based on prompt characteristics
        response_parts = []
        
        # Base response
        response_parts.append(f"Response to: \{test_case.get('task', 'task')\}")
        
        # Add content based on prompt features
        if 'examples' in prompt.lower():
            response_parts.append("Following the example format provided.")
        
        if 'requirements' in prompt.lower():
            response_parts.append("Meeting all specified requirements.")
        
        if test_case.get('criteria', {}).get('keywords'):
            response_parts.append(f"Including keywords: \{', '.join(test_case['criteria']['keywords'][:3])\}")
        
        # Add some variety
        if random.random() > 0.5:
            response_parts.append("Additional relevant information here.")
        
        return " ".join(response_parts)
    
    def _extract_best_practices(self) -> List[str]:
        """Extract best practices from test results."""
        practices = []
        
        if not self.test_results:
            return practices
        
        # Analyze high performers
        high_performers = [r for r in self.test_results if r.overall_score > 0.75]
        
        if high_performers:
            # Check common patterns
            if all(r.metadata.get('has_examples') for r in high_performers):
                practices.append("Always include 1-3 relevant examples in prompts")
            
            if all(r.metadata.get('has_constraints') for r in high_performers):
                practices.append("Specify clear constraints and requirements")
            
            if all(r.metadata.get('has_format') for r in high_performers):
                practices.append("Define expected output format explicitly")
            
            # Check score patterns
            avg_scores = {}
            for metric in ['relevance', 'completeness', 'format_compliance']:
                scores = [r.scores.get(metric, 0) for r in high_performers]
                avg_scores[metric] = statistics.mean(scores) if scores else 0
            
            if avg_scores.get('relevance', 0) > 0.8:
                practices.append("Use specific, task-relevant language in instructions")
            
            if avg_scores.get('completeness', 0) > 0.8:
                practices.append("Break down complex tasks into clear sub-points")
        
        # Add general best practices
        practices.extend([
            "Test prompts with multiple variations before production use",
            "Monitor prompt performance over time for drift",
            "Maintain a library of proven prompt templates"
        ])
        
        return practices[:7]  # Return top 7 practices

# Demonstration
def demonstrate_prompt_testing():
    """Demonstrate the prompt testing framework with a marketing use case."""
    
    print("="*60)
    print("PROMPT ENGINEERING TESTING FRAMEWORK")
    print("="*60)
    
    # Initialize framework
    framework = PromptTestingFramework()
    
    # Create base prompt for product description
    base_prompt = PromptTemplate(
        name="product_description_base",
        instruction="Write a compelling product description for a smartwatch.",
        context="",
        examples=[],
        constraints=[],
        output_format="",
        tone=""
    )
    
    # Create variants
    variants = [
        # Add context
        base_prompt.create_variant(
            'context',
            'Target audience: fitness enthusiasts aged 25-40'
        ),
        
        # Add examples
        base_prompt.create_variant(
            'examples',
            [
                'Example: "Track your fitness journey with precision..."',
                'Example: "Never miss a beat with 24/7 heart monitoring..."'
            ]
        ),
        
        # Add constraints
        base_prompt.create_variant(
            'constraints',
            [
                'Include at least 3 key features',
                'Keep it under 100 words',
                'Mention battery life'
            ]
        ),
        
        # Add format specification
        base_prompt.create_variant(
            'output_format',
            'Start with a hook, list 3 features, end with call-to-action'
        ),
        
        # Add tone guidance
        base_prompt.create_variant(
            'tone',
            'Energetic and motivational'
        ),
        
        # Combined improvements
        PromptTemplate(
            name="product_description_optimized",
            instruction="Write a compelling product description for a smartwatch.",
            context="Target audience: fitness enthusiasts aged 25-40",
            examples=['Example: "Transform your fitness journey..."'],
            constraints=['Include 3 key features', 'Under 100 words', 'Mention battery life'],
            output_format='Hook, features, call-to-action',
            tone='Energetic and motivational'
        )
    ]
    
    # Define test cases
    test_cases = [
        {
            'task': 'smartwatch description',
            'criteria': {
                'keywords': ['fitness', 'track', 'heart', 'battery', 'smart'],
                'required_points': ['features', 'battery life', 'fitness tracking'],
                'format_check': ['!', '?', '-'],  # Excitement markers
                'target_length': 80,
                'tone': 'professional'
            }
        },
        {
            'task': 'smartwatch for athletes',
            'criteria': {
                'keywords': ['performance', 'training', 'goals', 'metrics'],
                'required_points': ['GPS', 'water resistant', 'long battery'],
                'target_length': 100,
                'tone': 'urgent'
            }
        }
    ]
    
    # Test all variants
    print("\\nTesting prompt variants...")
    results = framework.test_prompt_variants(base_prompt, variants, test_cases)
    
    # Display results
    print("\\n" + "="*60)
    print("TEST RESULTS")
    print("="*60)
    
    for result in sorted(results, key=lambda x: x.overall_score, reverse=True):
        print(f"\\nPrompt: \{result.prompt_name\}")
        print(f"Overall Score: \{result.overall_score:.2%\}")
        print("Scores by Metric:")
        for metric, score in result.scores.items():
            bar = '█' * int(score * 10)
            print(f"  \{metric:20\} \{score:.2f\} \{bar\}")
    
    # Analyze patterns
    print("\\n" + "="*60)
    print("PATTERN ANALYSIS")
    print("="*60)
    
    analysis = framework.analyze_results(results)
    
    print(f"\\nBest Performer: \{analysis['best_performer']['name']\}")
    print(f"Score: \{analysis['best_performer']['score']:.2%\}")
    print(f"Strengths: \{', '.join(analysis['best_performer']['strengths'])\}")
    
    print(f"\\nWorst Performer: \{analysis['worst_performer']['name']\}")
    print(f"Score: \{analysis['worst_performer']['score']:.2%\}")
    print(f"Weaknesses: \{', '.join(analysis['worst_performer']['weaknesses'])\}")
    
    # Generate and display report
    report = framework.generate_report("Marketing Copy Prompt Optimization")
    print(report)
    
    # Show prompt library
    print("\\n" + "="*60)
    print("PROMPT LIBRARY")
    print("="*60)
    print(f"High-performing prompts saved: \{len(framework.prompt_library)\}")
    
    for name, template in framework.prompt_library.items():
        print(f"\\n\{name\}:")
        print(f"  Components: \{', '.join([k for k in ['context', 'examples', 'constraints', 'format', 'tone'] if getattr(template, k.replace('format', 'output_format'), None)])\}")

# Run demonstration
demonstrate_prompt_testing()`,
      testCases: [
        {
          input: 'Base prompt vs. variants with different components',
          expected: 'Variants with examples and constraints score higher',
          description: 'Component impact analysis',
        },
        {
          input: 'Multiple test cases with different criteria',
          expected: 'Consistent scoring across different scenarios',
          description: 'Robust evaluation methodology',
        },
        {
          input: 'Pattern analysis across results',
          expected: 'Identifies which prompt elements improve performance',
          description: 'Best practice extraction',
        },
      ],
    },
  ],
  quiz: {
    id: 'llm-1-quiz',
    title: 'Module 1 Comprehensive Quiz',
    description: 'Test your understanding of LLM foundations, implications, and core concepts',
    questions: [], // Questions will be selected from the question bank
    questionBank: llmFoundationsModule1QuestionBank, // Use the full 100-question bank
    questionsPerQuiz: 10,
    passingScore: 8, // 80% of 10 questions
  },
  resources: [
    {
      id: 'r1-1',
      title: 'Attention Is All You Need (Original Paper)',
      url: 'https://arxiv.org/abs/1706.03762',
      type: 'document',
      description: 'The foundational paper introducing transformers',
    },
    {
      id: 'r1-2',
      title: 'The Illustrated Transformer',
      url: 'https://jalammar.github.io/illustrated-transformer/',
      type: 'link',
      description: 'Visual guide to understanding transformer architecture',
    },
    {
      id: 'r1-3',
      title: 'GPT-3: Language Models are Few-Shot Learners',
      url: 'https://arxiv.org/abs/2005.14165',
      type: 'document',
      description: 'OpenAI paper introducing GPT-3',
    },
    {
      id: 'r1-4',
      title: 'Stanford HAI AI Index Report',
      url: 'https://aiindex.stanford.edu/',
      type: 'link',
      description: 'Comprehensive annual report on AI progress and impact',
    },
    {
      id: 'r1-5',
      title: 'EU AI Act',
      url: 'https://digital-strategy.ec.europa.eu/en/policies/regulatory-framework-ai',
      type: 'link',
      description: 'European Union regulatory framework for AI',
    },
    {
      id: 'r1-6',
      title: 'McKinsey: The Economic Potential of Generative AI',
      url: 'https://www.mckinsey.com/capabilities/mckinsey-digital/our-insights/the-economic-potential-of-generative-ai-the-next-productivity-frontier',
      type: 'link',
      description: 'Analysis of economic impact of generative AI',
    },
    {
      id: 'r1-7',
      title: 'OpenAI Tokenizer',
      url: 'https://platform.openai.com/tokenizer',
      type: 'link',
      description: 'Interactive tool to understand tokenization',
    },
    {
      id: 'r1-8',
      title: 'Attention Visualization - BertViz',
      url: 'https://github.com/jessevig/bertviz',
      type: 'link',
      description: 'Tool for visualizing attention in transformer models',
    },
  ],
};
